/* map-engine.js: the Artist Map (window.SongSueyMapEngine).

   Contract
   --------
   create(stage, { config, index, world, state, shell, onStateChange, layer })
     stage     the .tool-stage element. It must hold the hosts stageMarkup()
               writes (map.njk prints them by hand for the page); anything
               missing is created.
     config    the page's `tool` object (data-tool-config). config.map is
               src/_data/mapConfig.js: defaults for every URL key, spin speed,
               cluster cell, pin sizes, zoom range, DPR caps, timings.
     index     /assets/data/map-index.json (column arrays, see place-index.js).
     world     /assets/geo/world-land-110m.json, TopoJSON.
     state     the shell's live state object (strings; toggles are "1"/"0").
     shell     the mounted SongSueyToolShell instance: set(), results(),
               track(), fetchJson() for the borders file.
     onStateChange(patch)  optional; the engine writes lat, lng, zoom, focus,
               tour, scene, panel through shell.set(patch, { silent: true }) and
               calls this afterwards.
     layer     true inside the overlay: lower DPR cap, no auto-spin on a place.
   returns { setState(state, changedKeys), getState(), focus(target), flyTo(view),
             rows(), startTour(), stopTour(), action(key), destroy() }
     focus(target)  "artist:<id>" | "place:<path>" | "" (clears)
     flyTo({ lat, lng, zoom, ms })  animated unless reduced motion asks otherwise
     rows()   the filtered artist rows the shell exports

   schema({ defaults, counts }) is pure and returns the control schema for the
   panel; src/map.11tydata.js runs this file under Node's vm to build the page
   front matter from it, so the page and the overlay share one declaration.
   stageMarkup() is the stage's inner HTML, also pure. openOverlay(opts) opens
   the layer from any page (the launcher in nav.js calls it after loading the
   vendor files, tool-shell.js and this file).

   Render loop
   -----------
   One 2D canvas at a capped device pixel ratio. Nothing draws at rest: every
   change calls invalidate(), which schedules one requestAnimationFrame. While
   something moves (drag, inertia, morph, fly, tour, spin) the frame reschedules
   itself; spin frames are held to 30 fps (20 while the watchdog has the quality
   down), the rest run at the display rate. The world layer (ground, graticule,
   land, borders, night) is drawn into an offscreen canvas keyed on everything
   that changes it, so a frame whose view did not move only blits the cached
   layer. During motion it is drawn at half resolution from the thinner motion
   copy of the land (world-land-110m-motion.json, fetched after the first paint)
   and drawn full from the full land once on settle; five slow frames in a row
   drop it to a third, thirty fast ones bring it back. Pins are projected into
   typed arrays each frame and hidden past the horizon by a depth test on
   precomputed unit vectors. Labels are placed on settle only and repainted from
   that placement on spin frames; the URL position is written on settle. The
   focused pin's pulse is a CSS-animated ring in the DOM, so a focused map at
   rest schedules no frames at all. An IntersectionObserver and visibilitychange
   stop the loop when the stage is off screen or the tab is hidden; the loop
   resumes through invalidate() when either comes back.

   Projection: d3.geoProjection over a raw function that mixes the orthographic
   raw with the flat raw (Natural Earth, Equal Earth or equirectangular) by a
   factor t; the globe is t = 0, the flat map t = 1, and the morph eases t over
   config.map.morphMs with the clip angle opening from 90 degrees to none. The
   drag keeps north up: the geographic point picked at pointerdown is solved
   back under the pointer each move (two unknowns, longitude then latitude), so
   the point under the finger stays put and the globe never rolls.

   Plain browser script, no bundler; loads after d3-array, d3-geo,
   topojson-client and tool-shell.js. Colors come from map.css tokens read with
   getComputedStyle at create; nothing here names a color. */
(function (global) {
	"use strict";

	var RAD = Math.PI / 180;
	var DEG = 180 / Math.PI;
	var SPRITE = "/assets/icons/sprite.svg#";
	var GOLDEN_ANGLE = 2.399963;

	// The facets the /artists directory uses (src/artists/index.njk), same keys.
	var GENRE_FACETS = [
		{ label: "Pop", key: "pop" },
		{ label: "Rock", key: "rock" },
		{ label: "Hip-Hop", key: "hip-hop|rap|trap" },
		{ label: "R&B / Soul", key: "r&b|soul|funk" },
		{ label: "Country", key: "country|bluegrass" },
		{ label: "Electronic", key: "electronic|electropop|edm|dance" },
		{ label: "Metal", key: "metal" },
		{ label: "Jazz", key: "jazz|big band|traditional pop|swing" }
	];
	var TIER_FACETS = ["Complete Artist", "Artist", "Songwriter", "Collaborator", "Co-Performer", "Singer / Performer"];
	var ERA_FACETS = [2020, 2010, 2000, 1990, 1980, 1970, 1960, 1950, 1940, 1930];
	// Where the globe starts and where the reset button returns: the United States,
	// which holds most of the roster (the owner's call).
	var DEFAULT_CENTER = { lat: 38.5, lng: -97 };
	var HELP_KEYS = [
		["Arrows", "Turn the globe, or pan the map"],
		["+ / -", "Zoom in and out"],
		["0", "Back to the whole world"],
		["g", "Switch between globe and flat map"],
		["n / p", "Next and previous artist in the list"],
		["Enter", "Open the focused artist's page"],
		["f", "Fly to the focused pin"],
		["Space", "Pause or resume the spin and the tour"],
		["Esc", "Clear the focus, close this card"],
		["?", "This list"]
	];

	/* ---------- pure helpers ---------- */

	function esc(value) {
		return String(value == null ? "" : value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
	}
	function icon(name) {
		return '<svg class="icon" focusable="false" aria-hidden="true"><use href="' + SPRITE + name + '"></use></svg>';
	}
	function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
	function lerp(a, b, t) { return a + (b - a) * t; }
	function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
	function wrapLng(lng) { return ((lng + 540) % 360) - 180; }
	function fmt(n, d) { var s = Number(n).toFixed(d == null ? 2 : d); return s.indexOf(".") === -1 ? s : s.replace(/\.?0+$/, ""); }
	function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
	function parseHex(hex) {
		var m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
		if (!m) return null;
		var n = parseInt(m[1], 16);
		return [n >> 16 & 255, n >> 8 & 255, n & 255];
	}
	function rgba(rgb, a) { return "rgba(" + rgb[0] + "," + rgb[1] + "," + rgb[2] + "," + a + ")"; }
	function mixHex(a, b, t) {
		var x = parseHex(a) || [128, 128, 128], y = parseHex(b) || x;
		return "rgb(" + Math.round(lerp(x[0], y[0], t)) + "," + Math.round(lerp(x[1], y[1], t)) + "," + Math.round(lerp(x[2], y[2], t)) + ")";
	}
	function joinNames(names) {
		if (names.length <= 1) return names.join("");
		return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
	}
	function plural(n, word) { return n + " " + word + (n === 1 ? "" : "s"); }

	/* ---------- schema (pure; also run under Node by src/map.11tydata.js) ---------- */

	function schema(opts) {
		opts = opts || {};
		var d = opts.defaults || {};
		var counts = opts.counts || {};
		var def = function (k, fallback) { return has(d, k) && d[k] != null ? String(d[k]) : fallback; };
		var count = function (group, key) { return counts[group] && has(counts[group], key) ? counts[group][key] : undefined; };
		var opt = function (value, label, group, key) {
			var o = { value: value, label: label };
			var c = count(group, key == null ? value : key);
			if (c != null) o.count = c;
			return o;
		};
		return [
			{ section: "View", key: "view", kind: "segmented", label: "View", tip: "Globe turns like a planet. Flat map lays the whole world out at once.", default: def("view", "globe"), options: [
				{ value: "globe", label: "Globe", icon: "fa-earth-americas" },
				{ value: "flat", label: "Flat map", icon: "fa-map" }
			] },
			{ key: "proj", kind: "select", label: "Flat projection", tip: "How the flat map stretches the world. Natural Earth keeps shapes honest, Equal Earth keeps areas honest, Equirectangular is a plain grid.", default: def("proj", "natural"), help: "Only read when the view is flat.", options: [
				{ value: "natural", label: "Natural Earth" },
				{ value: "equal", label: "Equal Earth" },
				{ value: "equirect", label: "Equirectangular" }
			] },
			{ key: "color", kind: "segmented", label: "Color pins by", tip: "What the pin colors mean: authorship tier, main genre, the decade the act formed, or band versus solo.", default: def("color", "tier"), options: [
				{ value: "tier", label: "Tier" },
				{ value: "genre", label: "Genre" },
				{ value: "era", label: "Era" },
				{ value: "type", label: "Type" }
			] },
			{ key: "size", kind: "select", label: "Size pins by", tip: "What the size of a pin counts: albums on file, tracks on file, the authorship score, or nothing at all.", default: def("size", "albums"), options: [
				{ value: "albums", label: "Albums on file" },
				{ value: "tracks", label: "Tracks on file" },
				{ value: "score", label: "Authorship score" },
				{ value: "equal", label: "All the same" }
			] },
			{ section: "Roster", key: "q", kind: "search", label: "Search", tip: "Type an artist, a city or a country to narrow the roster.", default: def("q", ""), placeholder: "Artist, city or country" },
			{ key: "genre", kind: "chips", label: "Genre", tip: "Show only acts in one genre bucket. All shows everyone.", default: def("genre", ""), options: [{ value: "", label: "All" }].concat(GENRE_FACETS.map(function (f) { return opt(f.key, f.label, "genre"); })) },
			{ key: "tier", kind: "chips", label: "Tier", tip: "Show only acts in these authorship tiers. Pick several to combine them.", default: def("tier", ""), multiple: true, help: "Pick more than one to combine them.", options: TIER_FACETS.map(function (t) { return opt(t.toLowerCase(), t, "tier"); }) },
			{ key: "era", kind: "chips", label: "Era", tip: "Show only acts that formed in these decades. Pick several to combine them.", default: def("era", ""), multiple: true, options: ERA_FACETS.map(function (y) { return opt(String(y), y + "s", "era"); }) },
			{ key: "country", kind: "select", label: "Country", tip: "Show only acts from one country.", default: def("country", ""), options: [{ value: "", label: "All countries" }] },
			{ key: "type", kind: "segmented", label: "Type", tip: "Bands, solo acts, or both.", default: def("type", ""), options: [
				{ value: "", label: "All" },
				{ value: "band", label: "Bands" },
				{ value: "solo", label: "Solo" }
			] },
			{ section: "Layers", key: "labels", kind: "segmented", label: "Place labels", tip: "Which place names print when the map is still. Auto names the busiest towns, Cities names every town with a pin, All adds regions and countries, None keeps the map clean.", default: def("labels", "auto"), options: [
				{ value: "auto", label: "Auto" },
				{ value: "cities", label: "Cities" },
				{ value: "all", label: "All" },
				{ value: "none", label: "None" }
			] },
			{ key: "spin", kind: "toggle", label: "Turn the globe when idle", tip: "The globe turns slowly on its own until you touch it, and again once you stop.", default: def("spin", "1") },
			{ key: "cluster", kind: "toggle", label: "Group nearby pins", tip: "Pins that would overlap merge into one bubble with a count. Click a bubble to zoom into it.", default: def("cluster", "1") },
			{ key: "grid", kind: "toggle", label: "Graticule", tip: "The faint lines of latitude and longitude.", default: def("grid", "1") },
			{ key: "borders", kind: "toggle", label: "Country borders", tip: "Country outlines, from a second outline file.", default: def("borders", "0"), help: "Loads a second outline file, about 38 KB." },
			{ key: "arcs", kind: "toggle", label: "Collaborator arcs on focus", tip: "When an artist is focused, lines join them to the hometowns of their collaborators.", default: def("arcs", "1") },
			{ key: "shade", kind: "segmented", label: "Daylight", tip: "Darken the half of the globe where it is night right now.", default: def("shade", "none"), options: [
				{ value: "none", label: "Off" },
				{ value: "day", label: "Night side" }
			] },
			{ section: "Advanced", key: "tour-start", kind: "button", label: "Start the tour", icon: "fa-play" },
			{ key: "reset-view", kind: "button", label: "Reset the view", icon: "fa-rotate-left" },
			{ key: "help", kind: "button", label: "Keyboard shortcuts", icon: "fa-keyboard" },
			{ key: "lat", kind: "hidden", default: def("lat", "") },
			{ key: "lng", kind: "hidden", default: def("lng", "") },
			{ key: "zoom", kind: "hidden", default: def("zoom", "") },
			{ key: "focus", kind: "hidden", default: def("focus", "") },
			{ key: "tour", kind: "hidden", default: def("tour", "0") },
			{ key: "scene", kind: "hidden", default: def("scene", "0") },
			{ key: "panel", kind: "hidden", default: def("panel", "1") }
		];
	}

	// The stage's inner HTML. src/map.njk prints the same hosts by hand so the page
	// carries them without a script; the layer writes them from here.
	function stageMarkup() {
		return '<div class="map-stage" data-map-stage tabindex="0" role="application" aria-label="Artist map. Drag to turn, zoom with the buttons, press ? for the keys.">' +
			'<div class="map-canvas" data-map-canvas></div>' +
			'<div class="map-toolbar" data-map-toolbar></div>' +
			'<div class="tool-tip map-tip" data-map-tip aria-hidden="true"></div>' +
			'<div class="map-legend" data-map-legend aria-hidden="true"></div>' +
			'<div class="map-focus" data-map-focus hidden></div>' +
			'<div class="map-tour" data-map-tour hidden></div>' +
			'<div class="map-help" data-map-help hidden></div>' +
			'<div class="map-pulse" data-map-pulse hidden aria-hidden="true"><span></span></div>' +
			'<p class="map-touch-hint" data-map-touch-hint hidden>Tap the map to explore it</p>' +
			'<p class="sr-only" data-map-live aria-live="polite"></p>' +
			'<p class="map-loading" data-map-loading>Loading the map</p>' +
			"</div>" +
			'<ul class="tool-results map-results" data-tool-results></ul>';
	}

	/* ---------- shared across engine instances ---------- */

	// The decoded land and the tables built from the index survive across overlay
	// opens: nav.js and the shell cache the fetched files, so the object identity
	// is stable and the decode and the table build are paid once per page.
	var memoLand = typeof WeakMap === "function" ? new WeakMap() : null;
	var memoTables = typeof WeakMap === "function" ? new WeakMap() : null;
	function memo(map, key, build) {
		if (!map || !key || typeof key !== "object") return build();
		var hit = map.get(key);
		if (!hit) { hit = build(); map.set(key, hit); }
		return hit;
	}
	function decodeLand(topojson, world) {
		if (!world || !world.objects || !world.objects.land) return null;
		var land = topojson.feature(world, world.objects.land);
		if (land.type === "FeatureCollection") land = topojson.merge(world, world.objects.land.geometries);
		return land;
	}
	// Everything the index gives that no control changes: artist and place
	// records, the place tree, unit vectors for the depth test, collaborator
	// links, country counts. Per-view state (filters, projection) stays in create().
	function buildTables(index) {
		var ac = index.cols.artists || [], pc = index.cols.places || [];
		var col = function (cols, name) { return cols.indexOf(name); };
		var A = { id: col(ac, "id"), name: col(ac, "name"), type: col(ac, "type"), score: col(ac, "score"), tier: col(ac, "tier"), cls: col(ac, "cls"), genre: col(ac, "genre"), genres: col(ac, "genres"), era: col(ac, "era"), albums: col(ac, "albums"), tracks: col(ac, "tracks"), lat: col(ac, "lat"), lng: col(ac, "lng"), city: col(ac, "city"), region: col(ac, "region"), country: col(ac, "country"), cc: col(ac, "cc"), place: col(ac, "place"), url: col(ac, "url"), wikidata: col(ac, "wikidata") };
		var P = { path: col(pc, "path"), url: col(pc, "url"), level: col(pc, "level"), name: col(pc, "name"), label: col(pc, "label"), lat: col(pc, "lat"), lng: col(pc, "lng"), artists: col(pc, "artists"), avg: col(pc, "avg"), cc: col(pc, "cc"), parent: col(pc, "parent") };
		var places = [], placeByPath = {};
		index.places.forEach(function (r) {
			var p = { path: r[P.path], url: r[P.url], level: r[P.level], name: r[P.name], label: r[P.label], lat: +r[P.lat], lng: +r[P.lng], artists: +r[P.artists], avg: +r[P.avg], cc: r[P.cc], parent: r[P.parent], shown: 0, members: [] };
			places.push(p);
			placeByPath[p.path] = p;
		});
		var artists = [], artistById = {};
		var n = index.artists.length;
		var ux = new Float64Array(n), uy = new Float64Array(n), uz = new Float64Array(n);
		var coordGroups = {};
		index.artists.forEach(function (r, k) {
			var a = { i: k, id: r[A.id], name: r[A.name], type: r[A.type], score: +r[A.score], tier: r[A.tier], cls: r[A.cls], genre: r[A.genre], genres: r[A.genres] || [], era: r[A.era], albums: +r[A.albums], tracks: +r[A.tracks], lat: +r[A.lat], lng: +r[A.lng], city: r[A.city], region: r[A.region], country: r[A.country], cc: r[A.cc], place: r[A.place], url: r[A.url], wikidata: r[A.wikidata], chain: [], group: null, gk: 0 };
			a.placeLabel = [a.city, a.region, a.country].filter(Boolean).join(", ");
			a.search = (a.name + " " + a.placeLabel).toLowerCase();
			for (var p = placeByPath[a.place]; p; p = p.parent ? placeByPath[p.parent] : null) { a.chain.push(p); p.members.push(k); }
			var lam = a.lng * RAD, phi = a.lat * RAD;
			ux[k] = Math.cos(phi) * Math.cos(lam);
			uy[k] = Math.cos(phi) * Math.sin(lam);
			uz[k] = Math.sin(phi);
			var key = a.lat.toFixed(4) + "," + a.lng.toFixed(4);
			(coordGroups[key] = coordGroups[key] || []).push(k);
			artists.push(a);
			artistById[a.id] = a;
		});
		Object.keys(coordGroups).forEach(function (key) {
			var g = coordGroups[key];
			if (g.length < 2) return;
			g.forEach(function (k, j) { artists[k].group = g; artists[k].gk = j; });
		});
		var collab = [], i;
		for (i = 0; i < n; i++) collab.push([]);
		(index.collab || []).forEach(function (r) {
			if (artists[r[0]] && artists[r[1]]) collab[r[0]].push({ j: r[1], tracks: +r[2] });
		});
		var countries = {};
		artists.forEach(function (a) { if (a.cc) countries[a.cc] = countries[a.cc] || { cc: a.cc, name: a.country, n: 0 }; if (a.cc) countries[a.cc].n++; });
		var maxAlbums = 1, maxTracks = 1;
		artists.forEach(function (a) { if (a.albums > maxAlbums) maxAlbums = a.albums; if (a.tracks > maxTracks) maxTracks = a.tracks; });
		return { n: n, artists: artists, artistById: artistById, places: places, placeByPath: placeByPath, ux: ux, uy: uy, uz: uz, collab: collab, countries: countries, maxAlbums: maxAlbums, maxTracks: maxTracks };
	}

	/* ---------- the engine ---------- */

	function create(stage, opts) {
		opts = opts || {};
		var d3 = global.d3;
		var topojson = global.topojson;
		if (!d3 || !d3.geoProjection || !topojson) throw new Error("map-engine: d3-geo and topojson-client must load first");
		var config = opts.config || {};
		var cfg = config.map || {};
		var defaults = cfg.defaults || {};
		var shell = opts.shell;
		var state = opts.state || (shell && shell.state) || {};
		var isLayer = !!opts.layer;
		var reduceMotion = !!(global.matchMedia && global.matchMedia("(prefers-reduced-motion: reduce)").matches);
		var coarse = !!(global.matchMedia && global.matchMedia("(pointer: coarse)").matches);
		var num = function (k, fallback) { var v = Number(cfg[k]); return isFinite(v) && cfg[k] != null ? v : fallback; };
		var SPIN_DEG_S = num("spinDegPerSec", 4);
		var IDLE_MS = num("idleResumeS", 6) * 1000;
		var CELL = num("clusterCellPx", 28);
		var LABEL_MAX = num("labelMax", 36);
		var PIN_MIN = num("pinMinPx", 2.5);
		var PIN_MAX = num("pinMaxPx", 14);
		var HIT = coarse ? Math.max(12, num("hitRadiusPx", 12)) : Math.max(6, num("hitRadiusPx", 12) / 2);
		var ZOOM_MIN = num("zoomMin", 0.6);
		var ZOOM_MAX = num("zoomMax", 8);
		var DPR_CAP = isLayer ? num("dprCapLayer", 1.5) : num("dprCap", 2);
		var FRAME_BUDGET = num("frameBudgetMs", 12);
		var MORPH_MS = reduceMotion ? 0 : num("morphMs", 900);
		var FLY_MS = reduceMotion ? 0 : num("flyMs", 1400);
		var TOUR_HOLD_MS = num("tourHoldS", 6) * 1000;

		/* hosts */
		function host(sel, html) {
			var el = stage.querySelector(sel);
			if (el) return el;
			var tmp = document.createElement("div");
			tmp.innerHTML = html;
			el = tmp.firstChild;
			(stage.querySelector("[data-map-stage]") || stage).appendChild(el);
			return el;
		}
		if (!stage.querySelector("[data-map-stage]")) stage.innerHTML = stageMarkup();
		var mapEl = stage.querySelector("[data-map-stage]");
		var canvasHost = host("[data-map-canvas]", '<div class="map-canvas" data-map-canvas></div>');
		var toolbarEl = host("[data-map-toolbar]", '<div class="map-toolbar" data-map-toolbar></div>');
		var tipEl = host("[data-map-tip]", '<div class="tool-tip map-tip" data-map-tip aria-hidden="true"></div>');
		var legendEl = host("[data-map-legend]", '<div class="map-legend" data-map-legend aria-hidden="true"></div>');
		var focusEl = host("[data-map-focus]", '<div class="map-focus" data-map-focus hidden></div>');
		var tourEl = host("[data-map-tour]", '<div class="map-tour" data-map-tour hidden></div>');
		var helpEl = host("[data-map-help]", '<div class="map-help" data-map-help hidden></div>');
		var pulseEl = host("[data-map-pulse]", '<div class="map-pulse" data-map-pulse hidden aria-hidden="true"><span></span></div>');
		var touchHintEl = host("[data-map-touch-hint]", '<p class="map-touch-hint" data-map-touch-hint hidden>Tap the map to explore it</p>');
		var liveEl = host("[data-map-live]", '<p class="sr-only" data-map-live aria-live="polite"></p>');
		var loadingEl = stage.querySelector("[data-map-loading]");
		if (loadingEl) loadingEl.hidden = true;
		var canvas = document.createElement("canvas");
		canvas.className = "map-canvas__el";
		canvasHost.innerHTML = "";
		canvasHost.appendChild(canvas);
		var ctx = canvas.getContext("2d");
		var off = document.createElement("canvas");
		var offCtx = off.getContext("2d");

		/* colors from map.css */
		var css = global.getComputedStyle(mapEl);
		var token = function (name, fallback) { var v = css.getPropertyValue(name).trim(); return v || fallback || "#888"; };
		var C = {
			ocean: token("--map-ocean"), land: token("--map-land"), landEdge: token("--map-land-edge"), border: token("--map-border"),
			graticule: token("--map-graticule"), sphere: token("--map-sphere"), glow: token("--map-glow"), night: token("--map-night"),
			label: token("--map-label"), halo: token("--map-halo"), rim: token("--map-rim"), arc: token("--map-arc"), focus: token("--map-focus"),
			cluster: token("--map-cluster"), other: token("--map-cat-other"), eraFrom: token("--map-era-from"), eraTo: token("--map-era-to"),
			band: token("--map-type-band"), solo: token("--map-type-solo"), cat: [], tier: []
		};
		var i;
		for (i = 1; i <= 8; i++) C.cat.push(token("--map-cat-" + i));
		for (i = 1; i <= 6; i++) C.tier.push(token("--map-tier-" + i));
		var glowRgb = parseHex(C.glow) || [86, 176, 255];
		var arcRgb = parseHex(C.arc) || [61, 214, 245];
		var FONT = css.fontFamily || "sans-serif";
		var MONO = token("--font-mono", "monospace");

		/* index */
		var index = opts.index || { cols: {}, artists: [], places: [], collab: [] };
		var world = opts.world;
		var tables = memo(memoTables, index, function () { return buildTables(index); });
		var n = tables.n, artists = tables.artists, artistById = tables.artistById, places = tables.places, placeByPath = tables.placeByPath;
		var ux = tables.ux, uy = tables.uy, uz = tables.uz, collab = tables.collab, countries = tables.countries, maxAlbums = tables.maxAlbums, maxTracks = tables.maxTracks;
		var placeHasPage = function (p) { return !!(p && p.url && p.url.indexOf("#") === -1); };

		/* geometry */
		var sphere = { type: "Sphere" }, graticule = d3.geoGraticule10(), borders = null, bordersPending = false;
		// The same 10 degree lines with a third of the points, for moving frames.
		var graticuleMotion = d3.geoGraticule().precision(7.5)();
		var land = world ? memo(memoLand, world, function () { return decodeLand(topojson, world); }) : null;
		// The motion copy (about a third of the points) is drawn while the view
		// moves; it arrives after the first paint and the full land stands in
		// until then. Same folder, same name with a -motion suffix.
		var landMotion = opts.worldMotion ? memo(memoLand, opts.worldMotion, function () { return decodeLand(topojson, opts.worldMotion); }) : null;
		var motionUrl = ((config.world && config.world[0]) || "/assets/geo/world-land-110m.json").replace(/\.json$/, "-motion.json");
		if (land && !landMotion && shell && shell.fetchJson) {
			shell.fetchJson(motionUrl).then(function (topo) {
				if (!destroyed) landMotion = memo(memoLand, topo, function () { return decodeLand(topojson, topo); });
			}, function () { /* the full land draws every frame, as before */ });
		}
		var FLAT_RAW = { natural: d3.geoNaturalEarth1Raw, equal: d3.geoEqualEarthRaw, equirect: d3.geoEquirectangularRaw };

		/* view state */
		var view = {
			lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng, zoom: 1,
			t: state.view === "flat" ? 1 : 0, proj: FLAT_RAW[state.proj] ? state.proj : "natural",
			w: 0, h: 0, dpr: 1, fitGlobe: 1, fitFlat: 1
		};
		var projection = null, path = null, offPath = null, rawT = -1, rawProj = "";
		var px = new Float32Array(n), py = new Float32Array(n), pr = new Float32Array(n), pv = new Uint8Array(n);
		var visible = [];        // filtered artist indices, draw order (big first)
		var visibleSet = new Uint8Array(n);
		var clusters = [];
		var spreadGroups = {};
		var labelsDrawn = [], labelsStale = true;   // placement cache, and whether a spin frame must place afresh
		var focusRef = null;     // { kind: "artist"|"place", a | p }
		var hoverRef = null;
		var pinColor = new Array(n), pinRadius = new Float32Array(n);
		var destroyed = false;

		/* motion state */
		var dragging = null, inertia = null, anims = [], tour = null, spinning = false, hoverArc = null;
		var lastInteraction = 0, idleTimer = null, rafId = 0, active = true, pageVisible = !document.hidden;
		var lastFrameAt = 0, lastStepAt = 0, slowFrames = 0, fastFrames = 0, quality = 1, motionQuality = 0.5, settleTimer = null, needSettle = false;
		var worldKey = "", lastDrawQ = 1, shadeTimer = null, sunPoint = null;
		// Per-frame scratch, allocated once: the phyllotaxis flags and the cluster
		// cells (integer keys, see drawPins).
		var spread = new Uint8Array(n), cells = typeof Map === "function" ? new Map() : null;
		// Phones: the stage starts out letting one finger scroll the page and takes
		// the touch over after a tap (see setCaptured). Wide screens and fine
		// pointers capture from the start.
		var touchGate = coarse && !!(global.matchMedia && global.matchMedia("(max-width: 575px)").matches);
		var captured = !touchGate, tapCandidate = null;

		function now() { return global.performance && performance.now ? performance.now() : Date.now(); }

		/* ---------- sizing and projection ---------- */

		function resize() {
			// Layout size, not the bounding rect: the layer opens inside a dialog
			// that scales up from 0.985, and a rect read mid-animation would size the
			// canvas 1.5% small and put every pin off its hit area.
			var w = Math.max(1, mapEl.clientWidth), h = Math.max(1, mapEl.clientHeight);
			view.dpr = Math.min(global.devicePixelRatio || 1, DPR_CAP);
			if (w === view.w && h === view.h && canvas.width === Math.round(w * view.dpr)) return;
			view.w = w; view.h = h;
			canvas.width = Math.round(w * view.dpr);
			canvas.height = Math.round(h * view.dpr);
			view.fitGlobe = Math.min(w, h) / 2 * 0.94;
			var fits = {};
			Object.keys(FLAT_RAW).forEach(function (k) {
				fits[k] = d3.geoProjection(FLAT_RAW[k]).fitSize([w * 0.96, h * 0.96], sphere).scale();
			});
			view.fits = fits;
			rawT = -1;
			labelsStale = true;
			invalidate();
		}
		function mixRaw(t, flatKey) {
			var a = d3.geoOrthographicRaw, b = FLAT_RAW[flatKey] || FLAT_RAW.natural;
			if (t <= 0) return a;
			if (t >= 1) return b;
			return function (l, p) {
				var pa = a(l, p), pb = b(l, p);
				return [pa[0] + (pb[0] - pa[0]) * t, pa[1] + (pb[1] - pa[1]) * t];
			};
		}
		function scaleFor(t) {
			var flat = (view.fits && view.fits[view.proj]) || view.fitGlobe;
			return lerp(view.fitGlobe, flat, t) * view.zoom;
		}
		function clipFor(t) { return t >= 1 ? null : 90 + 89.9 * t; }
		function updateProjection() {
			var t = view.t;
			if (rawT !== t || rawProj !== view.proj || !projection) {
				projection = d3.geoProjection(mixRaw(t, view.proj)).precision(0.5);
				path = d3.geoPath(projection, ctx);
				offPath = d3.geoPath(projection, offCtx);
				rawT = t; rawProj = view.proj;
			}
			var clip = clipFor(t);
			projection.clipAngle(clip);
			projection.rotate([-view.lng, -view.lat * (1 - t), 0]);
			projection.scale(scaleFor(t));
			projection.translate([view.w / 2, view.h / 2]);
			if (t > 0) {
				var c = projection([view.lng, view.lat]);
				if (c && isFinite(c[1])) projection.translate([view.w / 2, view.h / 2 + (view.h / 2 - c[1])]);
			}
			return projection;
		}
		// Depth of every pin in view space: 1 at the centre, 0 on the horizon.
		function depthThreshold() { var clip = clipFor(view.t); return clip == null ? -2 : Math.cos(clip * RAD); }
		function projectPins() {
			var r = projection.rotate();
			var dl = r[0] * RAD, dp = r[1] * RAD;
			var cl = Math.cos(dl), sl = Math.sin(dl), cp = Math.cos(dp), sp = Math.sin(dp);
			var thr = depthThreshold();
			for (var k = 0; k < n; k++) {
				if (!visibleSet[k]) { pv[k] = 0; continue; }
				var x1 = ux[k] * cl - uy[k] * sl;
				var depth = x1 * cp - uz[k] * sp;
				if (depth < thr) { pv[k] = 0; continue; }
				var q = projection([artists[k].lng, artists[k].lat]);
				if (!q || !isFinite(q[0]) || !isFinite(q[1])) { pv[k] = 0; continue; }
				px[k] = q[0]; py[k] = q[1]; pv[k] = 1;
			}
		}

		/* ---------- north-up drag solver ---------- */

		// Screen point -> view-space unit vector on the orthographic sphere.
		function viewVector(sx, sy) {
			var tr = projection.translate(), k = projection.scale();
			var u = (sx - tr[0]) / k, v = (tr[1] - sy) / k;
			var rr = u * u + v * v;
			if (rr > 0.9999) { var s = Math.sqrt(0.9999 / rr); u *= s; v *= s; rr = 0.9999; }
			return [Math.sqrt(1 - rr), u, v];
		}
		// Rotation [lambda, phi] (degrees) that puts the geographic point [lng, lat]
		// under the view vector D, with no roll. Longitude first (the y component
		// survives the latitude rotation), then latitude from the x/z pair.
		function solveRotate(lng, lat, D, prevLambda) {
			var lp = lng * RAD, fp = lat * RAD, cfp = Math.cos(fp);
			var s = cfp < 1e-6 ? 0 : clamp(D[1] / cfp, -1, 1);
			var base = Math.asin(s);
			var cands = [base, Math.PI - base];
			var best = null, bestD = Infinity;
			for (var c = 0; c < 2; c++) {
				var lam = cands[c] - lp;
				var dd = Math.abs(Math.atan2(Math.sin(lam - prevLambda), Math.cos(lam - prevLambda)));
				if (dd < bestD) { bestD = dd; best = cands[c]; }
			}
			var xq = cfp * Math.cos(best), zq = Math.sin(fp);
			var phi = Math.atan2(D[2], D[0]) - Math.atan2(zq, xq);
			phi = Math.atan2(Math.sin(phi), Math.cos(phi));
			phi = clamp(phi, -Math.PI / 2, Math.PI / 2);
			var lamOut = Math.atan2(Math.sin(best - lp), Math.cos(best - lp));
			return [lamOut * DEG, phi * DEG];
		}
		function invertScreen(sx, sy) {
			if (!projection.invert) return null;
			var g = projection.invert([sx, sy]);
			if (!g || !isFinite(g[0]) || !isFinite(g[1])) return null;
			if (view.t <= 0) {
				var tr = projection.translate(), k = projection.scale();
				var dx = sx - tr[0], dy = sy - tr[1];
				if (dx * dx + dy * dy > k * k) return null;
			}
			return [wrapLng(g[0]), clamp(g[1], -90, 90)];
		}
		function setCenter(lng, lat) {
			view.lng = wrapLng(lng);
			view.lat = clamp(lat, -89.5, 89.5);
		}
		// Keep the geographic point g under screen point s after a zoom or a drag.
		function pinPointUnder(g, sx, sy) {
			updateProjection();
			if (view.t <= 0) {
				var r = solveRotate(g[0], g[1], viewVector(sx, sy), -view.lng * RAD);
				setCenter(-r[0], -r[1]);
			} else if (view.t >= 1) {
				var q = projection(g);
				if (!q) return;
				var c = invertScreen(view.w / 2 + (q[0] - sx), view.h / 2 + (q[1] - sy));
				if (c) setCenter(c[0], c[1]);
			}
			updateProjection();
		}

		/* ---------- filters, colors, rows ---------- */

		function tierIndex(tier) { var k = TIER_FACETS.map(function (t) { return t.toLowerCase(); }).indexOf(tier); return k === -1 ? 5 : k; }
		function genreSlot(genre) { for (var g = 0; g < GENRE_FACETS.length; g++) if (GENRE_FACETS[g].label === genre) return g; return -1; }
		function colorFor(a) {
			var mode = state.color || "tier";
			if (mode === "genre") { var g = genreSlot(a.genre); return g === -1 ? C.other : C.cat[g]; }
			if (mode === "era") { if (a.era == null) return C.other; return mixHex(C.eraFrom, C.eraTo, clamp((a.era - 1930) / 90, 0, 1)); }
			if (mode === "type") return a.type === "solo" ? C.solo : C.band;
			return C.tier[tierIndex(a.tier)];
		}
		function radiusFor(a) {
			var mode = state.size || "albums", t;
			if (mode === "tracks") t = Math.sqrt(a.tracks / maxTracks);
			else if (mode === "score") t = a.score / 100;
			else if (mode === "equal") t = 0.35;
			else t = Math.sqrt(a.albums / maxAlbums);
			return PIN_MIN + (PIN_MAX - PIN_MIN) * clamp(t, 0, 1);
		}
		function restyle() {
			for (var k = 0; k < n; k++) { pinColor[k] = colorFor(artists[k]); pinRadius[k] = radiusFor(artists[k]); }
			renderLegend();
			invalidate();
		}
		function matches(a) {
			var q = (state.q || "").trim().toLowerCase();
			if (q && a.search.indexOf(q) === -1) return false;
			if (state.genre && a.genres.indexOf(state.genre) === -1) return false;
			if (state.tier && state.tier.split(",").indexOf(a.tier) === -1) return false;
			if (state.era && state.era.split(",").indexOf(String(a.era)) === -1) return false;
			if (state.country && a.cc !== state.country) return false;
			if (state.type && a.type !== state.type) return false;
			return true;
		}
		function refilter(announce) {
			visible = [];
			places.forEach(function (p) { p.shown = 0; });
			for (var k = 0; k < n; k++) {
				var on = matches(artists[k]);
				visibleSet[k] = on ? 1 : 0;
				if (!on) continue;
				visible.push(k);
				artists[k].chain.forEach(function (p) { p.shown++; });
			}
			visible.sort(function (a, b) { return pinRadius[b] - pinRadius[a] || artists[a].name.localeCompare(artists[b].name); });
			publishRows();
			// The shell's count line is a role=status region and already announces
			// the same number, so the engine only speaks when there is no count line.
			if (announce && !(stage.parentNode && stage.parentNode.querySelector("[data-tool-count]"))) say(visible.length === n ? "All " + plural(n, "artist") + " shown" : visible.length + " of " + plural(n, "artist") + " shown");
			var gone = focusRef && focusRef.kind === "artist" && !visibleSet[focusRef.a.i] ? focusRef.a : null;
			if (gone) { setFocus("", "silent"); say(gone.name + " is hidden by the current filters, so the focus is cleared."); }
			// The cached label placement counted the old roster; the next frame places afresh.
			labelsStale = true;
			invalidate();
		}
		function rowFor(a) {
			return {
				id: a.id, name: a.name, type: a.type, score: a.score, cls: a.cls, tier: a.tier, genre: a.genre, era: a.era == null ? "" : a.era + "s",
				albums: a.albums, tracks: a.tracks, city: a.city || "", region: a.region || "", country: a.country || "", cc: a.cc || "",
				lat: a.lat, lng: a.lng, url: "/artist/" + a.id + "/", place_url: a.url || "", meta: a.placeLabel || ""
			};
		}
		var currentRows = [];
		function publishRows() {
			var order = visible.slice().sort(function (a, b) { return artists[b].score - artists[a].score || artists[a].name.localeCompare(artists[b].name); });
			currentRows = order.map(function (k) { return rowFor(artists[k]); });
			if (shell) shell.results(currentRows, { total: n, noun: "artists" });
			markFocusedRow();
		}
		function markFocusedRow() {
			var list = stage.querySelector("[data-tool-results]");
			if (!list) return;
			var links = list.querySelectorAll(".tool-result");
			for (var j = 0; j < links.length; j++) {
				var a = links[j].querySelector(".tool-result__name");
				var href = a && a.getAttribute("href");
				var on = !!(focusRef && focusRef.kind === "artist" && href === "/artist/" + focusRef.a.id + "/");
				links[j].classList.toggle("is-focused", on);
			}
		}
		function fillCountryOptions() {
			var el = stage.parentNode ? stage.parentNode.querySelector('select[data-tool-control="country"]') : null;
			if (!el || el.options.length > 1) return;
			var list = Object.keys(countries).map(function (cc) { return countries[cc]; }).sort(function (a, b) { return b.n - a.n || a.name.localeCompare(b.name); });
			list.forEach(function (c) {
				var o = document.createElement("option");
				o.value = c.cc;
				o.textContent = c.name + " (" + c.n + ")";
				el.appendChild(o);
			});
			if (state.country) el.value = state.country;
		}
		// Live counts on the chips: the tier chips have none at build time (their
		// thresholds live in .eleventy.js), so every chip group is filled from the
		// index here, which also makes the numbers match the roster on the map.
		function fillChipCounts() {
			var root = stage.parentNode;
			if (!root) return;
			var counts = { genre: {}, tier: {}, era: {} };
			artists.forEach(function (a) {
				a.genres.forEach(function (g) { counts.genre[g] = (counts.genre[g] || 0) + 1; });
				counts.tier[a.tier] = (counts.tier[a.tier] || 0) + 1;
				if (a.era != null) counts.era[String(a.era)] = (counts.era[String(a.era)] || 0) + 1;
			});
			Object.keys(counts).forEach(function (key) {
				var group = root.querySelector('[data-tool-control="' + key + '"]');
				if (!group) return;
				var chips = group.querySelectorAll(".tool-chip");
				for (var j = 0; j < chips.length; j++) {
					var v = chips[j].getAttribute("data-value");
					if (!v) continue;
					var span = chips[j].querySelector(".tool-chip__count");
					if (!span) { span = document.createElement("span"); span.className = "tool-chip__count"; chips[j].appendChild(document.createTextNode(" ")); chips[j].appendChild(span); }
					span.textContent = counts[key][v] || 0;
				}
			});
		}

		/* ---------- legend ---------- */

		function renderLegend() {
			var mode = state.color || "tier";
			var html = "", items = [];
			if (mode === "genre") {
				html += '<p class="map-legend__title">Genre</p>';
				GENRE_FACETS.forEach(function (f, g) { items.push({ c: C.cat[g], l: f.label }); });
				items.push({ c: C.other, l: "Other", muted: true });
			} else if (mode === "type") {
				html += '<p class="map-legend__title">Type</p>';
				items.push({ c: C.band, l: "Band" }, { c: C.solo, l: "Solo artist" });
			} else if (mode === "era") {
				html += '<p class="map-legend__title">Decade formed</p><div class="map-legend__ramp"></div><div class="map-legend__ends"><span>1930s</span><span>2020s</span></div>';
			} else {
				html += '<p class="map-legend__title">Authorship tier</p>';
				TIER_FACETS.forEach(function (t, k) { items.push({ c: C.tier[k], l: t }); });
			}
			if (items.length) {
				html += '<ul class="map-legend__list">';
				items.forEach(function (it) { html += '<li class="map-legend__item' + (it.muted ? " is-muted" : "") + '"><span class="map-legend__swatch" data-swatch></span>' + esc(it.l) + "</li>"; });
				html += "</ul>";
			}
			var sizeWord = { albums: "albums on file", tracks: "tracks on file", score: "authorship score", equal: "" }[state.size || "albums"];
			if (sizeWord) html += '<p class="map-legend__size">Pin size: ' + sizeWord + "</p>";
			legendEl.innerHTML = html;
			var sw = legendEl.querySelectorAll("[data-swatch]");
			for (var j = 0; j < sw.length && j < items.length; j++) sw[j].style.setProperty("--swatch", items[j].c);
		}

		/* ---------- drawing ---------- */

		function worldQuality() { return (dragging || inertia || anims.length || spinning) ? Math.min(quality, motionQuality) : 1; }
		function drawWorld(q) {
			var w = view.w, h = view.h, s = view.dpr * q;
			var ow = Math.max(1, Math.round(w * s)), oh = Math.max(1, Math.round(h * s));
			if (off.width !== ow || off.height !== oh) { off.width = ow; off.height = oh; }
			var o = offCtx;
			o.setTransform(s, 0, 0, s, 0, 0);
			o.clearRect(0, 0, w, h);
			var t = view.t, k = projection.scale(), tr = projection.translate();
			// ground and atmosphere
			if (t < 1) {
				var glowA = 0.55 * (1 - t);
				var grad = o.createRadialGradient(tr[0], tr[1], k * 0.96, tr[0], tr[1], k * 1.08);
				grad.addColorStop(0, rgba(glowRgb, glowA));
				grad.addColorStop(0.5, rgba(glowRgb, glowA * 0.35));
				grad.addColorStop(1, rgba(glowRgb, 0));
				o.fillStyle = grad;
				o.beginPath();
				o.arc(tr[0], tr[1], k * 1.08, 0, Math.PI * 2);
				o.fill();
			}
			o.beginPath();
			offPath(sphere);
			o.fillStyle = C.ocean;
			o.fill();
			if (state.grid !== "0" && !(q < 0.5)) {
				o.beginPath();
				offPath(q < 1 ? graticuleMotion : graticule);
				o.strokeStyle = C.graticule;
				o.lineWidth = 0.6;
				o.stroke();
			}
			var shape = q < 1 && landMotion ? landMotion : land;
			if (shape) {
				o.beginPath();
				offPath(shape);
				o.fillStyle = C.land;
				o.fill();
				o.strokeStyle = C.landEdge;
				o.lineWidth = 0.5;
				o.stroke();
			}
			if (state.borders === "1" && borders) {
				o.beginPath();
				offPath(borders);
				o.strokeStyle = C.border;
				o.lineWidth = 0.6;
				o.stroke();
			}
			if (state.shade === "day" && sunPoint) {
				o.beginPath();
				offPath(d3.geoCircle().center([sunPoint[0] + 180, -sunPoint[1]]).radius(90).precision(2)());
				o.fillStyle = rgba(parseHex(C.night) || [2, 6, 15], 0.5);
				o.fill();
			}
			o.beginPath();
			offPath(sphere);
			o.strokeStyle = C.sphere;
			o.lineWidth = 1;
			o.stroke();
		}
		function drawPins() {
			var cx = ctx, thr = state.cluster !== "0";
			clusters = [];
			var k, j, a;
			// phyllotaxis offsets for groups that share one coordinate
			spread.fill(0);
			for (j = 0; j < visible.length; j++) {
				k = visible[j];
				if (!pv[k]) continue;
				a = artists[k];
				if (!a.group) continue;
				var open = !thr || view.zoom >= 4 || spreadGroups[a.group[0]] || (focusRef && focusRef.kind === "artist" && a.group.indexOf(focusRef.a.i) !== -1);
				if (!open) continue;
				var rr = 7 + 5 * Math.sqrt(a.gk), ang = a.gk * GOLDEN_ANGLE;
				px[k] += rr * Math.cos(ang);
				py[k] += rr * Math.sin(ang);
				spread[k] = 1;
			}
			// grid clustering; the cell key is one integer (column and row offset
			// so a pin left of or above the canvas still lands in a cell of its own)
			var single = [];
			var bucket = cells || {};
			if (cells) cells.clear();
			for (j = 0; j < visible.length; j++) {
				k = visible[j];
				if (!pv[k]) continue;
				if (!thr || spread[k] || view.zoom >= 6) { single.push(k); continue; }
				var key = (Math.floor(px[k] / CELL) + 32768) * 65536 + (Math.floor(py[k] / CELL) + 32768);
				var list = cells ? cells.get(key) : bucket[key];
				if (!list) { list = []; if (cells) cells.set(key, list); else bucket[key] = list; }
				list.push(k);
			}
			var bucketDone = function (m) {
				if (m.length < 2) { single.push(m[0]); return; }
				var sx = 0, sy = 0, tiers = {}, best = null;
				m.forEach(function (kk) { sx += px[kk]; sy += py[kk]; var t = artists[kk].tier; tiers[t] = (tiers[t] || 0) + 1; if (!best || tiers[t] > tiers[best]) best = t; });
				clusters.push({ x: sx / m.length, y: sy / m.length, members: m, r: Math.min(15, 7 + 1.5 * Math.sqrt(m.length)), color: state.color === "tier" || !state.color ? C.tier[tierIndex(best)] : pinColor[m[0]] });
			};
			if (cells) cells.forEach(bucketDone); else Object.keys(bucket).forEach(function (key) { bucketDone(bucket[key]); });
			// pins, big first so small ones land on top; the glow is a gradient per
			// pin, so it is skipped while the view moves and the world is at half
			// resolution anyway
			var glow = worldQuality() >= 1 || single.length <= 120;
			for (j = 0; j < single.length; j++) {
				k = single[j];
				var r = pinRadius[k], color = pinColor[k];
				if (glow) {
					var rgb = parseHex(color) || (color.indexOf("rgb") === 0 ? color.slice(4, -1).split(",").map(Number) : [200, 200, 200]);
					var g = cx.createRadialGradient(px[k], py[k], r * 0.6, px[k], py[k], r * 2.4);
					g.addColorStop(0, rgba(rgb, 0.45));
					g.addColorStop(1, rgba(rgb, 0));
					cx.fillStyle = g;
					cx.beginPath();
					cx.arc(px[k], py[k], r * 2.4, 0, Math.PI * 2);
					cx.fill();
				}
				cx.beginPath();
				cx.arc(px[k], py[k], r, 0, Math.PI * 2);
				cx.fillStyle = color;
				cx.fill();
				cx.lineWidth = 1;
				cx.strokeStyle = C.rim;
				cx.stroke();
			}
			for (j = 0; j < clusters.length; j++) {
				var c = clusters[j];
				cx.beginPath();
				cx.arc(c.x, c.y, c.r + 4, 0, Math.PI * 2);
				cx.fillStyle = rgba(parseHex(c.color) || [200, 200, 200], 0.28);
				cx.fill();
				cx.beginPath();
				cx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
				cx.fillStyle = c.color;
				cx.fill();
				cx.lineWidth = 1;
				cx.strokeStyle = C.rim;
				cx.stroke();
				cx.fillStyle = C.rim;
				cx.font = "700 " + (c.members.length > 99 ? 10 : 11) + "px " + FONT;
				cx.textAlign = "center";
				cx.textBaseline = "middle";
				cx.fillText(String(c.members.length), c.x, c.y + 0.5);
			}
			// focus ring; the pulse around it is the DOM ring placePulse() moves
			if (focusRef && focusRef.kind === "artist" && pv[focusRef.a.i]) {
				k = focusRef.a.i;
				cx.beginPath();
				cx.arc(px[k], py[k], pinRadius[k] + 2, 0, Math.PI * 2);
				cx.strokeStyle = C.focus;
				cx.lineWidth = 1.5;
				cx.stroke();
			}
			if (focusRef && focusRef.kind === "place") {
				var p = focusRef.p;
				cx.strokeStyle = rgba(parseHex(C.focus) || [255, 255, 255], p.members.length > 40 ? 0.3 : 0.7);
				cx.lineWidth = 1;
				for (j = 0; j < p.members.length; j++) {
					k = p.members[j];
					if (!pv[k] || !visibleSet[k]) continue;
					cx.beginPath();
					cx.arc(px[k], py[k], pinRadius[k] + 2.5, 0, Math.PI * 2);
					cx.stroke();
				}
			}
		}
		var arcSamples = [];
		function drawArcs(tNow) {
			arcSamples = [];
			if (!focusRef || focusRef.kind !== "artist" || state.arcs === "0") return;
			var a = focusRef.a, links = collab[a.i];
			if (!links.length) return;
			var thr = depthThreshold();
			var r = projection.rotate(), dl = r[0] * RAD, dp = r[1] * RAD;
			var cl = Math.cos(dl), sl = Math.sin(dl), cp = Math.cos(dp), sp = Math.sin(dp);
			var progress = reduceMotion || !focusRef.since ? 1 : clamp((tNow - focusRef.since) / 700, 0, 1);
			// One interpolator and one sample array per link for the life of the
			// focus, not per frame.
			if (!focusRef.arcs) {
				var heaviest = 1;
				links.forEach(function (l) { if (l.tracks > heaviest) heaviest = l.tracks; });
				focusRef.arcs = links.map(function (l) { var b = artists[l.j]; return { link: l, interp: d3.geoInterpolate([a.lng, a.lat], [b.lng, b.lat]), pts: [], weight: l.tracks / heaviest }; });
			}
			focusRef.arcs.forEach(function (arc) {
				var l = arc.link, interp = arc.interp, pts = arc.pts;
				var steps = 40;
				pts.length = 0;
				ctx.beginPath();
				var pen = false;
				for (var s = 0; s <= steps * progress; s++) {
					var g = interp(s / steps);
					var lam = g[0] * RAD, phi = g[1] * RAD;
					var x = Math.cos(phi) * Math.cos(lam), y = Math.cos(phi) * Math.sin(lam), z = Math.sin(phi);
					var depth = (x * cl - y * sl) * cp - z * sp;
					var q = depth >= thr ? projection(g) : null;
					if (!q || !isFinite(q[0])) { pen = false; continue; }
					var lift = view.t < 1 ? Math.sin(Math.PI * s / steps) * 0.06 * projection.scale() * (1 - view.t) : 0;
					var yy = q[1] - lift;
					if (pen) ctx.lineTo(q[0], yy); else ctx.moveTo(q[0], yy);
					pen = true;
					pts.push(q[0], yy);
				}
				ctx.strokeStyle = rgba(arcRgb, 0.35 + 0.5 * arc.weight);
				ctx.lineWidth = hoverArc === l ? 3 : 1.5;
				ctx.stroke();
				arcSamples.push(arc);
			});
		}
		// The pulse is a CSS-animated ring in the DOM, moved to the focused pin
		// after each draw; a focused map at rest therefore schedules no frames.
		function placePulse() {
			var k = focusRef && focusRef.kind === "artist" ? focusRef.a.i : -1;
			if (k === -1 || !pv[k] || !visibleSet[k]) { pulseEl.hidden = true; return; }
			var size = Math.round((pinRadius[k] + 3) * 2);
			pulseEl.style.setProperty("--pulse-size", size + "px");
			pulseEl.style.transform = "translate(" + px[k].toFixed(1) + "px," + py[k].toFixed(1) + "px)";
			pulseEl.hidden = false;
		}
		function labelBudget() {
			var base = LABEL_MAX * (1 + Math.log(Math.max(1, view.zoom)) / Math.LN2);
			return Math.round(base);
		}
		// Labels are placed on a rest frame (measureText and the occupancy grid run
		// once) and remembered as an offset from each place's projected point, so
		// a spin frame can repaint them where the globe has carried them.
		function labelFont(big) { return big ? "700 10px " + MONO : "600 11px " + FONT; }
		function labelPaintStart() {
			ctx.textBaseline = "middle";
			ctx.lineJoin = "round";
			ctx.lineWidth = 3;
			ctx.strokeStyle = C.halo;
			ctx.fillStyle = C.label;
		}
		function paintLabels() {
			if (!labelsDrawn.length) return;
			var thr = depthThreshold();
			var r = projection.rotate(), dl = r[0] * RAD, dp = r[1] * RAD;
			var cl = Math.cos(dl), sl = Math.sin(dl), cp = Math.cos(dp), sp = Math.sin(dp);
			labelPaintStart();
			for (var j = 0; j < labelsDrawn.length; j++) {
				var l = labelsDrawn[j], p = l.p || l.a;
				var lam = p.lng * RAD, phi = p.lat * RAD;
				var x = Math.cos(phi) * Math.cos(lam), y = Math.cos(phi) * Math.sin(lam), z = Math.sin(phi);
				l.box = null;
				if ((x * cl - y * sl) * cp - z * sp < thr) continue;
				var q = projection([p.lng, p.lat]);
				if (!q || !isFinite(q[0])) continue;
				var sx = q[0] + l.ox, sy = q[1] + l.oy;
				var bx = l.align === "left" ? sx : l.align === "right" ? sx - l.tw : sx - l.tw / 2;
				var box = [bx - 2, sy - 7, bx + l.tw + 2, sy + 7];
				// The rest placement is remembered as an offset, but a spin frame can carry a
				// cluster bubble or a pin under the label; such a label sits this frame out.
				if (labelCovered(box)) continue;
				ctx.font = l.font;
				ctx.textAlign = l.align;
				if (l.big) ctx.globalAlpha = 0.75;
				ctx.strokeText(l.text, sx, sy);
				ctx.fillText(l.text, sx, sy);
				ctx.globalAlpha = 1;
				l.box = box;
			}
		}
		function labelCovered(b) {
			var j, c;
			for (j = 0; j < clusters.length; j++) {
				c = clusters[j];
				if (b[0] < c.x + c.r && b[2] > c.x - c.r && b[1] < c.y + c.r && b[3] > c.y - c.r) return true;
			}
			for (j = 0; j < visible.length; j++) {
				var k = visible[j];
				if (!pv[k]) continue;
				var r = pinRadius[k];
				if (b[0] < px[k] + r && b[2] > px[k] - r && b[1] < py[k] + r && b[3] > py[k] - r) return true;
			}
			return false;
		}
		function placeLabels() {
			labelsDrawn = [];
			labelsStale = false;
			var mode = state.labels || "auto";
			if (mode === "none") return;
			var levels = mode === "cities" ? { city: 1, locality: 1 } : mode === "all" ? { country: 1, region: 1, city: 1, locality: 1 } : (view.zoom < 1.6 ? { country: 1, city: 1 } : { city: 1, locality: 1, region: 1 });
			// Auto at the whole-world zoom names the towns with company; one-artist
			// towns wait for the zoom, so the budget goes to the scenes first.
			var floor = mode === "auto" && view.zoom < 1.6 ? 2 : 1;
			var cands = places.filter(function (p) { return p.shown > 0 && levels[p.level] && isFinite(p.lat) && (p.level === "country" || p.shown >= floor); });
			cands.sort(function (a, b) { return b.shown - a.shown || a.name.localeCompare(b.name); });
			var budget = mode === "all" ? 200 : labelBudget();
			var thr = depthThreshold();
			var r = projection.rotate(), dl = r[0] * RAD, dp = r[1] * RAD;
			var cl = Math.cos(dl), sl = Math.sin(dl), cp = Math.cos(dp), sp = Math.sin(dp);
			var boxes = [];
			var k;
			// pins near the focus keep their space
			if (focusRef && focusRef.kind === "artist" && pv[focusRef.a.i]) {
				k = focusRef.a.i;
				boxes.push([px[k] - 40, py[k] - 14, px[k] + 40, py[k] + 14]);
			}
			clusters.forEach(function (c) { boxes.push([c.x - c.r - 2, c.y - c.r - 2, c.x + c.r + 2, c.y + c.r + 2]); });
			// Every drawn pin keeps its space as well, so a country label can never sit
			// on top of a town's dot (the reviewers caught UNITED KINGDOM over London).
			for (var pj = 0; pj < visible.length; pj++) {
				var pk = visible[pj];
				if (!pv[pk]) continue;
				var prad = pinRadius[pk] + 2;
				boxes.push([px[pk] - prad, py[pk] - prad, px[pk] + prad, py[pk] + prad]);
			}
			function free(b) {
				if (b[0] < 2 || b[1] < 2 || b[2] > view.w - 2 || b[3] > view.h - 2) return false;
				for (var j = 0; j < boxes.length; j++) { var o = boxes[j]; if (b[0] < o[2] && b[2] > o[0] && b[1] < o[3] && b[3] > o[1]) return false; }
				return true;
			}
			labelPaintStart();
			var drawn = 0;
			for (var c = 0; c < cands.length && drawn < budget; c++) {
				var p = cands[c];
				var lam = p.lng * RAD, phi = p.lat * RAD;
				var x = Math.cos(phi) * Math.cos(lam), y = Math.cos(phi) * Math.sin(lam), z = Math.sin(phi);
				if ((x * cl - y * sl) * cp - z * sp < thr) continue;
				var q = projection([p.lng, p.lat]);
				if (!q || !isFinite(q[0])) continue;
				var at = q;
				var big = p.level === "country" || p.level === "region";
				var text = big ? p.name.toUpperCase() : p.name;
				ctx.font = labelFont(big);
				var tw = ctx.measureText(text).width, th = 12;
				// A town folded into a cluster bubble labels the bubble's edge, not its centre.
				var gap = 8;
				for (var ci = 0; ci < clusters.length; ci++) {
					var cc = clusters[ci];
					if ((cc.x - q[0]) * (cc.x - q[0]) + (cc.y - q[1]) * (cc.y - q[1]) <= cc.r * cc.r) { gap = cc.r + 6; at = [cc.x, cc.y]; break; }
				}
				var slots = [[at[0] + gap, at[1], "left"], [at[0] - gap, at[1], "right"], [at[0], at[1] - gap - 4, "center"], [at[0], at[1] + gap + 4, "center"]];
				for (var s = 0; s < slots.length; s++) {
					var sx = slots[s][0], sy = slots[s][1], align = slots[s][2];
					var bx = align === "left" ? sx : align === "right" ? sx - tw : sx - tw / 2;
					var box = [bx - 2, sy - th / 2 - 1, bx + tw + 2, sy + th / 2 + 1];
					if (!free(box)) continue;
					ctx.textAlign = align;
					if (big) { ctx.globalAlpha = 0.75; }
					ctx.strokeText(text, sx, sy);
					ctx.fillText(text, sx, sy);
					ctx.globalAlpha = 1;
					boxes.push(box);
					labelsDrawn.push({ p: p, box: box, text: text, font: ctx.font, big: big, align: align, tw: tw, ox: sx - q[0], oy: sy - q[1] });
					drawn++;
					break;
				}
			}
			// the focused artist's own name
			if (focusRef && focusRef.kind === "artist" && pv[focusRef.a.i]) {
				k = focusRef.a.i;
				var fa = focusRef.a, fx = px[k] + pinRadius[k] + 8;
				ctx.font = "700 12px " + FONT;
				ctx.textAlign = "left";
				ctx.strokeText(fa.name, fx, py[k]);
				ctx.fillText(fa.name, fx, py[k]);
				var fq = projection([fa.lng, fa.lat]);
				if (fq) labelsDrawn.push({ a: fa, box: null, text: fa.name, font: ctx.font, big: false, align: "left", tw: ctx.measureText(fa.name).width, ox: fx - fq[0], oy: py[k] - fq[1] });
			}
		}
		// Everything the offscreen world depends on. Same key, same picture: the
		// frame blits the cached layer and skips the land stream entirely.
		function worldCacheKey(q) {
			var r = projection.rotate(), tr = projection.translate();
			return q + "|" + view.w + "x" + view.h + "@" + view.dpr + "|" + view.t.toFixed(4) + "|" + view.proj + "|" + r[0].toFixed(3) + "," + r[1].toFixed(3) + "|" + projection.scale().toFixed(2) + "|" + tr[0].toFixed(2) + "," + tr[1].toFixed(2) +
				"|" + (state.grid !== "0" ? "g" : "") + (state.borders === "1" && borders ? "b" : "") + (state.shade === "day" && sunPoint ? sunPoint[0].toFixed(2) + "," + sunPoint[1].toFixed(2) : "") + (q < 1 && landMotion ? "m" : "f");
		}
		function draw(tNow) {
			if (!view.w) resize();
			updateProjection();
			var q = worldQuality();
			var started = now();
			var key = worldCacheKey(q);
			if (key !== worldKey) { drawWorld(q); worldKey = key; }
			ctx.setTransform(1, 0, 0, 1, 0, 0);
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			ctx.imageSmoothingEnabled = true;
			ctx.drawImage(off, 0, 0, off.width, off.height, 0, 0, canvas.width, canvas.height);
			ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
			projectPins();
			drawArcs(tNow);
			drawPins();
			var still = !dragging && !inertia && !anims.length;
			if (q >= 1 && still && !spinning) placeLabels();
			else if (spinning && still) { if (labelsStale) placeLabels(); else paintLabels(); }
			else labelsDrawn = [];
			placePulse();
			lastDrawQ = q;
			var cost = now() - started;
			// The watchdog is hysteretic: five slow moving frames drop the world to a
			// third, thirty fast ones in a row bring it back, so a rough patch does
			// not pin the globe at a third until the next settle.
			if (q < 1) {
				if (cost > FRAME_BUDGET * 1.5) { fastFrames = 0; if (++slowFrames >= 5) { quality = 0.35; slowFrames = 0; } }
				else { slowFrames = 0; if (quality < 1 && ++fastFrames >= 30) { quality = 1; fastFrames = 0; } }
			}
		}

		/* ---------- scheduler ---------- */

		var dirty = false;
		function invalidate() {
			dirty = true;
			if (!rafId && active && pageVisible && !destroyed) rafId = global.requestAnimationFrame(frame);
		}
		function moving() { return !!(dragging || inertia || anims.length || spinning); }
		function frame(tNow) {
			rafId = 0;
			if (destroyed) return;
			// Off screen or in a hidden tab the loop parks here; the observer and
			// the visibility handler call invalidate() to pick it back up.
			if (!active || !pageVisible) { dirty = true; return; }
			// dt counts from the last step, not the last draw: a frame the throttle
			// skips below must not add its time to the spin twice.
			var dt = lastStepAt ? Math.min(100, tNow - lastStepAt) : 16;
			lastStepAt = tNow;
			stepAnims(tNow);
			stepInertia(dt);
			stepSpin(tNow, dt);
			var isMoving = moving();
			// Spin frames run at 30 fps, and at 20 while the watchdog has the world at a third.
			var slow = spinning && !dragging && !inertia && !anims.length;
			if (slow && tNow - lastFrameAt < (quality < 1 ? 48 : 30)) { rafId = global.requestAnimationFrame(frame); return; }
			lastFrameAt = tNow;
			draw(tNow);
			dirty = false;
			if (isMoving) {
				needSettle = true;
				rafId = global.requestAnimationFrame(frame);
				return;
			}
			if (needSettle) { needSettle = false; settle(); }
		}
		// The frame in which motion ends already draws at full quality (nothing is
		// moving when draw() asks), so settle only asks for another frame when the
		// last one was drawn reduced: the full world is drawn exactly once.
		function settle() {
			quality = 1;
			slowFrames = 0;
			fastFrames = 0;
			writePosition();
			if (lastDrawQ < 1) invalidate();
		}
		function writePosition() {
			if (!shell || !isFinite(view.lat) || !isFinite(view.lng) || !isFinite(view.zoom)) return;
			var patch = {};
			var atDefault = Math.abs(view.lat - DEFAULT_CENTER.lat) < 0.5 && Math.abs(wrapLng(view.lng - DEFAULT_CENTER.lng)) < 0.5;
			patch.lat = atDefault ? "" : fmt(view.lat, 2);
			patch.lng = atDefault ? "" : fmt(view.lng, 2);
			patch.zoom = Math.abs(view.zoom - 1) < 0.02 ? "" : fmt(view.zoom, 2);
			shell.set(patch, { silent: true });
			if (opts.onStateChange) opts.onStateChange(patch);
		}

		/* ---------- animation ---------- */

		function tween(ms, onFrame, onDone) {
			// An instant tween (reduced motion, ms 0) lands on its last frame with
			// both arguments at 1 and still settles, so the URL follows the view.
			if (ms <= 0) { onFrame(1, 1); if (onDone) onDone(); invalidate(); scheduleSettle(); return null; }
			var a = { start: now(), ms: ms, onFrame: onFrame, onDone: onDone };
			anims.push(a);
			invalidate();
			return a;
		}
		function stepAnims(tNow) {
			if (!anims.length) return;
			var keep = [];
			anims.forEach(function (a) {
				var t = clamp((tNow - a.start) / a.ms, 0, 1);
				a.onFrame(easeInOut(t), t);
				if (t < 1) keep.push(a); else if (a.onDone) a.onDone();
			});
			anims = keep;
		}
		function cancelAnims() { anims = []; }
		function flyTo(target) {
			target = target || {};
			cancelAnims();
			inertia = null;
			var from = { lat: view.lat, lng: view.lng, zoom: view.zoom };
			var to = { lat: target.lat == null ? from.lat : clamp(+target.lat, -89.5, 89.5), lng: target.lng == null ? from.lng : wrapLng(+target.lng), zoom: target.zoom == null ? from.zoom : clamp(+target.zoom, ZOOM_MIN, ZOOM_MAX) };
			var ms = target.ms != null ? target.ms : FLY_MS;
			var interp = d3.geoInterpolate([from.lng, from.lat], [to.lng, to.lat]);
			var dist = d3.geoDistance([from.lng, from.lat], [to.lng, to.lat]) * DEG;
			var dip = dist > 25 ? Math.min(0.6, dist / 180) : 0;
			return tween(reduceMotion ? 0 : ms, function (e, raw) {
				var g = interp(e);
				setCenter(g[0], g[1]);
				var z = Math.exp(lerp(Math.log(from.zoom), Math.log(to.zoom), e));
				if (dip) z *= 1 - dip * Math.sin(Math.PI * raw) * (1 - Math.abs(0.5 - raw) * 0.4);
				view.zoom = clamp(isFinite(z) ? z : to.zoom, ZOOM_MIN, ZOOM_MAX);
			}, target.onDone);
		}
		function morphTo(flat) {
			var target = flat ? 1 : 0;
			if (view.t === target) return;
			cancelAnims();
			inertia = null;
			var from = view.t;
			tween(MORPH_MS, function (e) { view.t = lerp(from, target, e); }, function () { view.t = target; });
		}

		/* ---------- inertia and spin ---------- */

		function stepInertia(dt) {
			if (!inertia) return;
			var decay = Math.pow(0.92, dt / 16);
			if (view.t <= 0) { setCenter(view.lng + inertia.vx * dt, view.lat + inertia.vy * dt); }
			else if (view.t >= 1) { updateProjection(); var c = invertScreen(view.w / 2 - inertia.vx * dt, view.h / 2 - inertia.vy * dt); if (c) setCenter(c[0], c[1]); }
			inertia.vx *= decay; inertia.vy *= decay;
			if (Math.abs(inertia.vx) + Math.abs(inertia.vy) < (view.t <= 0 ? 0.002 : 0.02)) inertia = null;
		}
		function spinAllowed() {
			return state.spin !== "0" && !reduceMotion && view.t <= 0 && !focusRef && !tour && !dragging && !inertia && !anims.length && !hoverRef && !helpOpen;
		}
		function stepSpin(tNow, dt) {
			var idle = now() - lastInteraction > IDLE_MS;
			var want = spinAllowed() && idle;
			if (want && !spinning) spinning = true;
			if (!want && spinning) spinning = false;
			if (spinning) setCenter(view.lng - SPIN_DEG_S * dt / 1000, view.lat);
		}
		function touch() {
			lastInteraction = now();
			if (spinning) { spinning = false; needSettle = true; }
			armIdle();
		}
		function armIdle() {
			if (idleTimer) clearTimeout(idleTimer);
			var wait = Math.max(0, IDLE_MS - (now() - lastInteraction)) + 20;
			idleTimer = setTimeout(function () { idleTimer = null; if (spinAllowed()) invalidate(); }, wait);
		}

		/* ---------- hit testing, tooltip, focus card ---------- */

		function hitTest(sx, sy) {
			var best = null, bestD = HIT * HIT, j, k, d;
			for (j = 0; j < clusters.length; j++) {
				var c = clusters[j];
				d = (c.x - sx) * (c.x - sx) + (c.y - sy) * (c.y - sy);
				if (d <= (c.r + 4) * (c.r + 4)) return { kind: "cluster", c: c };
			}
			for (j = visible.length - 1; j >= 0; j--) {
				k = visible[j];
				if (!pv[k]) continue;
				d = (px[k] - sx) * (px[k] - sx) + (py[k] - sy) * (py[k] - sy);
				var reach = Math.max(HIT, pinRadius[k] + 2);
				if (d <= reach * reach && d < bestD + 1) { best = { kind: "artist", a: artists[k] }; bestD = d; }
			}
			if (best) return best;
			for (j = 0; j < arcSamples.length; j++) {
				var pts = arcSamples[j].pts;
				for (k = 0; k < pts.length; k += 2) {
					d = (pts[k] - sx) * (pts[k] - sx) + (pts[k + 1] - sy) * (pts[k + 1] - sy);
					if (d <= 36) return { kind: "arc", link: arcSamples[j].link };
				}
			}
			for (j = 0; j < labelsDrawn.length; j++) {
				var b = labelsDrawn[j].box;
				if (!b || !labelsDrawn[j].p) continue;
				if (sx >= b[0] && sx <= b[2] && sy >= b[1] && sy <= b[3]) return { kind: "place", p: labelsDrawn[j].p };
			}
			return null;
		}
		function badge(a) { return '<span class="score-badge score-' + esc(a.cls || "none") + '">' + a.score + "%</span>"; }
		function placeLink(a) {
			if (!a.placeLabel) return "";
			return a.url ? '<a href="' + esc(a.url) + '">' + esc(a.placeLabel) + "</a>" : esc(a.placeLabel);
		}
		// The tooltip is rebuilt and measured only when the hovered thing changes;
		// a move within the same pin just moves it.
		var tipW = 0, tipH = 0;
		function showTip(hit, sx, sy, rebuild) {
			if (rebuild || !tipEl.classList.contains("is-on")) buildTip(hit);
			var x = sx + 14, y = sy + 14;
			if (x + tipW > view.w - 8) x = sx - tipW - 14;
			if (y + tipH > view.h - 8) y = sy - tipH - 14;
			tipEl.style.transform = "translate(" + Math.round(x) + "px," + Math.round(y) + "px)";
		}
		function buildTip(hit) {
			var html = "";
			if (hit.kind === "artist") {
				var a = hit.a;
				html = '<div class="map-tip__row"><span class="tool-tip__title">' + esc(a.name) + "</span>" + badge(a) + "</div>" +
					'<span class="map-tip__place">' + esc(a.placeLabel) + "</span>" +
					'<span class="tool-tip__meta">' + esc(TIER_FACETS[tierIndex(a.tier)]) + " · " + plural(a.albums, "album") + " · " + plural(a.tracks, "track") + "</span>" +
					'<span class="map-tip__hint">Click for links and collaborators</span>';
			} else if (hit.kind === "cluster") {
				var names = hit.c.members.slice(0, 5).map(function (k) { return artists[k].name; });
				var more = hit.c.members.length - names.length;
				html = '<span class="tool-tip__title">' + plural(hit.c.members.length, "artist") + " here</span>" +
					'<span class="tool-tip__meta">' + esc(names.join(", ")) + (more > 0 ? " and " + more + " more" : "") + "</span>" +
					'<span class="map-tip__hint">Click to zoom in</span>';
			} else if (hit.kind === "arc") {
				var b = artists[hit.link.j], f = focusRef && focusRef.kind === "artist" ? focusRef.a : null;
				html = '<span class="tool-tip__title">' + esc((f ? f.name + " and " : "") + b.name) + "</span>" +
					'<span class="tool-tip__meta">' + plural(hit.link.tracks, "shared track") + "</span>";
			} else if (hit.kind === "place") {
				html = '<span class="tool-tip__title">' + esc(hit.p.label) + "</span>" +
					'<span class="tool-tip__meta">' + plural(hit.p.shown, "artist") + " shown" + (hit.p.avg ? " · " + hit.p.avg + "% average" : "") + "</span>" +
					'<span class="map-tip__hint">' + (placeHasPage(hit.p) ? "Click for details and a link to its page" : "Click to zoom in") + "</span>";
			}
			tipEl.innerHTML = html;
			tipEl.classList.add("is-on");
			tipW = tipEl.offsetWidth; tipH = tipEl.offsetHeight;
		}
		function hideTip() { tipEl.classList.remove("is-on"); }
		function say(text) { liveEl.textContent = text; }

		// A re-render must not drop keyboard focus on the floor: the same button is
		// focused again in the new card, and clearing the card hands focus to the
		// stage.
		function renderFocus() {
			var act = focusEl.contains(document.activeElement) ? (document.activeElement.getAttribute("data-map-act") || "name") : null;
			mapEl.classList.toggle("is-focused", !!focusRef);
			if (stage.parentNode && stage.parentNode.classList) stage.parentNode.classList.toggle("is-focused", !!focusRef);
			if (!focusRef) { focusEl.hidden = true; focusEl.innerHTML = ""; pulseEl.hidden = true; markFocusedRow(); if (act) mapEl.focus(); return; }
			var html = "";
			if (focusRef.kind === "artist") {
				var a = focusRef.a;
				var links = collab[a.i];
				html = '<div class="map-focus__head"><div><span class="map-focus__eyebrow">' + (a.type === "solo" ? "Solo artist" : "Band") + (a.era != null ? " · " + a.era + "s" : "") + '</span><h3 class="map-focus__name"><a href="/artist/' + esc(a.id) + '/">' + esc(a.name) + "</a></h3></div>" +
					'<button class="map-focus__close" type="button" data-map-act="clear" aria-label="Clear the focus" data-tip="Clear the focus (Esc)">' + icon("fa-xmark") + "</button></div>" +
					'<p class="map-focus__place">' + placeLink(a) + "</p>" +
					'<p class="map-focus__stats">' + badge(a) + "<span>" + esc(TIER_FACETS[tierIndex(a.tier)]) + "</span><span>" + plural(a.albums, "album") + "</span><span>" + plural(a.tracks, "track") + "</span></p>" +
					'<div class="map-focus__actions">' +
						'<a class="map-focus__link map-focus__link--primary" href="/artist/' + esc(a.id) + '/">' + icon("fa-arrow-up-right-from-square") + " Artist page</a>" +
						'<button class="map-focus__btn" type="button" data-map-act="fly">' + icon("fa-location-crosshairs") + " Fly to</button>" +
						(links.length ? '<button class="map-focus__btn" type="button" data-map-act="arcs" aria-pressed="' + (state.arcs !== "0" ? "true" : "false") + '">' + icon("fa-share-nodes") + " " + plural(links.length, "collaborator") + "</button>" : "") +
					"</div>";
				if (links.length && state.arcs !== "0") {
					html += '<ul class="map-focus__collab">' + links.map(function (l) { var b = artists[l.j]; return '<li><a href="#" data-map-focus-artist="' + esc(b.id) + '">' + esc(b.name) + "</a><span>" + l.tracks + "</span></li>"; }).join("") + "</ul>";
				}
			} else {
				var p = focusRef.p;
				var lvl = p.level === "locality" ? "Neighbourhood" : p.level.charAt(0).toUpperCase() + p.level.slice(1);
				html = '<div class="map-focus__head"><div><span class="map-focus__eyebrow">' + lvl + '</span><h3 class="map-focus__name">' + (placeHasPage(p) ? '<a href="' + esc(p.url) + '">' + esc(p.name) + "</a>" : esc(p.name)) + "</h3></div>" +
					'<button class="map-focus__close" type="button" data-map-act="clear" aria-label="Clear the focus" data-tip="Clear the focus (Esc)">' + icon("fa-xmark") + "</button></div>" +
					(p.label !== p.name ? '<p class="map-focus__place">' + esc(p.label) + "</p>" : "") +
					'<p class="map-focus__stats"><span>' + plural(p.artists, "artist") + "</span><span>" + p.avg + "% average authorship</span>" + (p.shown !== p.artists ? "<span>" + p.shown + " shown</span>" : "") + "</p>" +
					'<div class="map-focus__actions">' +
						(p.url ? '<a class="map-focus__link map-focus__link--primary" href="' + esc(p.url) + '">' + icon("fa-arrow-up-right-from-square") + " Place page</a>" : "") +
						'<button class="map-focus__btn" type="button" data-map-act="fly">' + icon("fa-location-crosshairs") + " Fly to</button>" +
					"</div>";
			}
			focusEl.innerHTML = html;
			// While a tour runs its caption already names the scene, so the place card
			// stays hidden; the pins keep their focus styling through focusRef.
			focusEl.hidden = !!(tour && focusRef.kind === "place");
			markFocusedRow();
			if (act) {
				var back = (act !== "name" && focusEl.querySelector('[data-map-act="' + act + '"]')) || focusEl.querySelector(".map-focus__name a") || focusEl.querySelector("button");
				if (back) back.focus();
			}
		}
		function zoomForPlace(p) {
			if (p.level === "city" || p.level === "locality") return 6;
			var lat0 = 90, lat1 = -90, lng0 = 180, lng1 = -180;
			p.members.forEach(function (k) { var a = artists[k]; lat0 = Math.min(lat0, a.lat); lat1 = Math.max(lat1, a.lat); lng0 = Math.min(lng0, a.lng); lng1 = Math.max(lng1, a.lng); });
			var extent = Math.max(lat1 - lat0, (lng1 - lng0) * Math.cos(((lat0 + lat1) / 2) * RAD), 2);
			return clamp(110 / extent, 1.2, 6);
		}
		function setFocus(target, source) {
			var next = null;
			if (target && target.indexOf("artist:") === 0) { var a = artistById[target.slice(7)]; if (a) next = { kind: "artist", a: a, since: now() }; }
			else if (target && target.indexOf("place:") === 0) { var p = placeByPath[target.slice(6)]; if (p) next = { kind: "place", p: p }; }
			// An artist the filters hide has no pin to focus: the card, the URL and
			// the ring would all point at nothing, so the focus clears instead.
			var hidden = next && next.kind === "artist" && !visibleSet[next.a.i] ? next.a : null;
			if (hidden) next = null;
			var same = !!(focusRef && next && focusRef.kind === next.kind && (next.kind === "artist" ? focusRef.a === next.a : focusRef.p === next.p));
			if (same && focusRef.arcs) next.arcs = focusRef.arcs;
			focusRef = next;
			if (next && next.kind === "artist" && next.a.group) spreadGroups[next.a.group[0]] = true;
			renderFocus();
			if (shell) {
				shell.set("focus", next ? target : "", { silent: true });
				if (opts.onStateChange) opts.onStateChange({ focus: next ? target : "" });
			}
			if (next && !same && source !== "url-no-fly") {
				if (next.kind === "artist") flyTo({ lat: next.a.lat, lng: next.a.lng, zoom: Math.max(view.zoom, 3.5) });
				else flyTo({ lat: next.p.lat, lng: next.p.lng, zoom: zoomForPlace(next.p) });
			}
			if (next) say(next.kind === "artist" ? "Focused on " + next.a.name + ", " + next.a.placeLabel + ". " + next.a.score + "% authorship." : "Focused on " + next.p.label + ", " + plural(next.p.artists, "artist") + ".");
			else if (hidden) say(hidden.name + " is hidden by the current filters, so the focus is cleared.");
			else if (source !== "silent") say("Focus cleared");
			touch();
			invalidate();
		}
		function stepArtist(dir) {
			if (!currentRows.length) return;
			var at = -1;
			if (focusRef && focusRef.kind === "artist") for (var j = 0; j < currentRows.length; j++) if (currentRows[j].id === focusRef.a.id) { at = j; break; }
			at = (at + dir + currentRows.length) % currentRows.length;
			setFocus("artist:" + currentRows[at].id, "key");
		}

		/* ---------- help, panel, toolbar ---------- */

		var helpOpen = false;
		function renderHelp(open) {
			helpOpen = open;
			if (!open) {
				var inside = helpEl.contains(document.activeElement);
				helpEl.hidden = true;
				if (inside) mapEl.focus();
				return;
			}
			helpEl.innerHTML = '<h3 class="map-help__title">Keys <button class="map-focus__close" type="button" data-map-act="help-close" aria-label="Close">' + icon("fa-xmark") + "</button></h3>" +
				'<ul class="map-help__list">' + HELP_KEYS.map(function (k) { return "<li><kbd>" + esc(k[0]) + "</kbd></li><li>" + esc(k[1]) + "</li>"; }).join("") + "</ul>";
			helpEl.hidden = false;
			var btn = helpEl.querySelector("button");
			if (btn) btn.focus();
		}
		// One Escape, one layer: the help card, then the tour, then the focus, then
		// a phone's touch capture. False when there was nothing left to clear, which
		// is when the overlay may close.
		function escapeAction() {
			if (helpOpen) renderHelp(false);
			else if (fullscreen) setFullscreen(false);
			else if (tour) stopTour();
			else if (focusRef) setFocus("", "key");
			else if (touchGate && captured) setCaptured(false, true);
			else return false;
			return true;
		}
		function renderToolbar() {
			toolbarEl.innerHTML =
				'<button class="map-toolbar__btn" type="button" data-map-act="zoom-in" aria-label="Zoom in" data-tip="Zoom in (+)" data-tip-side="left">' + icon("fa-plus") + "</button>" +
				'<button class="map-toolbar__btn" type="button" data-map-act="zoom-out" aria-label="Zoom out" data-tip="Zoom out (-)" data-tip-side="left">' + icon("fa-minus") + "</button>" +
				'<button class="map-toolbar__btn" type="button" data-map-act="home" aria-label="Reset the view" data-tip="Reset the view (0)" data-tip-side="left">' + icon("fa-globe") + "</button>" +
				'<button class="map-toolbar__btn map-toolbar__btn--fullscreen" type="button" data-map-act="fullscreen" aria-label="Fullscreen" data-tip="Fullscreen" data-tip-side="left" aria-pressed="false">' + icon("fa-expand") + "</button>" +
				'<button class="map-toolbar__btn map-toolbar__btn--panel" type="button" data-map-act="panel" aria-label="Show or hide the controls" data-tip="Show or hide the controls" data-tip-side="left" aria-pressed="' + (state.panel !== "0" ? "true" : "false") + '">' + icon("fa-sliders") + "</button>" +
				'<button class="map-toolbar__btn map-toolbar__btn--release" type="button" data-map-act="release" aria-label="Done with the map, let the page scroll" data-tip="Done with the map: give the finger back to page scrolling (Esc)" data-tip-side="left" hidden>' + icon("fa-hand") + "</button>";
		}
		// Phones only (touchGate): until a tap, one finger scrolls the page over the
		// map (touch-action pan-y pinch-zoom in map.css). A tap captures the touch
		// for the map; the hand button in the toolbar, Escape, or scrolling the
		// stage out of view hands it back.
		function setCaptured(on, announce) {
			captured = !touchGate || !!on;
			mapEl.classList.toggle("is-captured", touchGate && captured);
			touchHintEl.hidden = !touchGate || captured;
			var btn = toolbarEl.querySelector('[data-map-act="release"]');
			if (btn) btn.hidden = !touchGate || !captured;
			if (announce && touchGate) say(captured ? "Map active. Drag to turn it, pinch to zoom. The hand button hands the finger back to the page." : "Map released. The page scrolls again.");
		}
		// Fullscreen pins the stage (and the corner cards) to the viewport; Escape or
		// the same button brings the page back. Off by default, never in the URL.
		var fullscreen = false;
		function setFullscreen(on) {
			var root = stage.parentNode;
			fullscreen = !!on;
			if (root && root.classList) root.classList.toggle("is-fullscreen", fullscreen);
			document.body.classList.toggle("tool-layer-open", fullscreen);
			var btn = toolbarEl.querySelector('[data-map-act="fullscreen"]');
			if (btn) {
				btn.setAttribute("aria-pressed", fullscreen ? "true" : "false");
				btn.setAttribute("aria-label", fullscreen ? "Exit fullscreen" : "Fullscreen");
				btn.setAttribute("data-tip", fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen");
				btn.innerHTML = icon(fullscreen ? "fa-compress" : "fa-expand");
			}
			say(fullscreen ? "Fullscreen map. Escape brings the page back." : "Back in the page.");
			resize();
			invalidate();
		}
		function applyPanel() {
			var root = stage.parentNode;
			if (root && root.classList) root.classList.toggle("is-panel-closed", state.panel === "0");
			var btn = toolbarEl.querySelector('[data-map-act="panel"]');
			if (btn) btn.setAttribute("aria-pressed", state.panel !== "0" ? "true" : "false");
		}
		function resetView() {
			cancelAnims();
			inertia = null;
			flyTo({ lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng, zoom: 1 });
		}
		function zoomBy(factor, sx, sy) {
			touch();
			cancelAnims();
			inertia = null;
			var g = sx != null ? invertScreen(sx, sy) : null;
			var z = clamp(view.zoom * factor, ZOOM_MIN, ZOOM_MAX);
			if (z === view.zoom) return;
			view.zoom = z;
			if (g) pinPointUnder(g, sx, sy);
			invalidate();
			scheduleSettle();
		}
		function scheduleSettle() {
			if (settleTimer) clearTimeout(settleTimer);
			settleTimer = setTimeout(function () { settleTimer = null; if (!moving()) settle(); }, 220);
		}

		/* ---------- pointer ---------- */

		var pointers = {};
		var lastTap = 0, lastTapX = 0, lastTapY = 0;
		function pointerPos(e) {
			var r = canvas.getBoundingClientRect();
			return [e.clientX - r.left, e.clientY - r.top];
		}
		function onPointerDown(e) {
			if (e.button != null && e.button !== 0) return;
			var p = pointerPos(e);
			// Not captured yet (a phone before the first tap): the finger belongs to
			// the page. Remember it so a clean tap can capture on release.
			if (e.pointerType === "touch" && !captured) { tapCandidate = { id: e.pointerId, x: p[0], y: p[1] }; return; }
			pointers[e.pointerId] = p;
			touch();
			hideTip();
			try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* no capture */ }
			var ids = Object.keys(pointers);
			if (ids.length === 2) {
				var a = pointers[ids[0]], b = pointers[ids[1]];
				dragging = { pinch: true, dist: Math.hypot(a[0] - b[0], a[1] - b[1]), zoom: view.zoom, mid: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] };
				dragging.g = invertScreen(dragging.mid[0], dragging.mid[1]);
				return;
			}
			cancelAnims();
			inertia = null;
			updateProjection();
			dragging = { id: e.pointerId, x0: p[0], y0: p[1], x: p[0], y: p[1], t0: now(), moved: false, samples: [], g: invertScreen(p[0], p[1]), lng0: view.lng, lat0: view.lat };
			mapEl.classList.add("is-dragging");
		}
		function onPointerMove(e) {
			var p = pointerPos(e);
			if (!dragging) {
				if (e.pointerType === "touch") return;
				var hit = hitTest(p[0], p[1]);
				var key = hit ? hit.kind + ":" + (hit.a ? hit.a.id : hit.p ? hit.p.path : hit.link ? hit.link.j : clusters.indexOf(hit.c)) : "";
				hoverArc = hit && hit.kind === "arc" ? hit.link : null;
				if (hit) showTip(hit, p[0], p[1], key !== hoverKey); else hideTip();
				if (key !== hoverKey) { hoverKey = key; hoverRef = hit; mapEl.classList.toggle("is-hover", !!hit); invalidate(); if (hit) touch(); }
				return;
			}
			if (!has(pointers, e.pointerId)) return;
			pointers[e.pointerId] = p;
			if (dragging.pinch) {
				var ids = Object.keys(pointers);
				if (ids.length < 2) return;
				var a = pointers[ids[0]], b = pointers[ids[1]];
				var dist = Math.hypot(a[0] - b[0], a[1] - b[1]);
				var mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
				view.zoom = clamp(dragging.zoom * dist / dragging.dist, ZOOM_MIN, ZOOM_MAX);
				if (dragging.g) pinPointUnder(dragging.g, mid[0], mid[1]);
				invalidate();
				return;
			}
			if (e.pointerId !== dragging.id) return;
			var dx = p[0] - dragging.x0, dy = p[1] - dragging.y0;
			if (!dragging.moved && dx * dx + dy * dy < 9) return;
			dragging.moved = true;
			var before = [view.lng, view.lat];
			if (view.t <= 0 && dragging.g) {
				pinPointUnder(dragging.g, p[0], p[1]);
			} else if (view.t >= 1) {
				updateProjection();
				var c = invertScreen(view.w / 2 - (p[0] - dragging.x), view.h / 2 - (p[1] - dragging.y));
				if (c) setCenter(c[0], c[1]);
			}
			var tNow = now();
			dragging.samples.push([tNow, view.lng - before[0], view.lat - before[1], p[0] - dragging.x, p[1] - dragging.y]);
			if (dragging.samples.length > 6) dragging.samples.shift();
			dragging.x = p[0]; dragging.y = p[1];
			invalidate();
		}
		function onPointerUp(e) {
			var p = pointerPos(e);
			if (tapCandidate && e.pointerId === tapCandidate.id) {
				var tap = e.type !== "pointercancel" && Math.abs(p[0] - tapCandidate.x) + Math.abs(p[1] - tapCandidate.y) < 12;
				tapCandidate = null;
				if (tap) { setCaptured(true, true); touch(); }
				return;
			}
			delete pointers[e.pointerId];
			try { canvas.releasePointerCapture(e.pointerId); } catch (err) { /* nothing held */ }
			if (!dragging) return;
			if (dragging.pinch) { if (!Object.keys(pointers).length) { dragging = null; mapEl.classList.remove("is-dragging"); scheduleSettle(); } return; }
			if (e.pointerId !== dragging.id) return;
			var d = dragging;
			dragging = null;
			mapEl.classList.remove("is-dragging");
			if (!d.moved) {
				var tNow = now();
				var dbl = tNow - lastTap < 320 && Math.abs(p[0] - lastTapX) < 24 && Math.abs(p[1] - lastTapY) < 24;
				lastTap = dbl ? 0 : tNow; lastTapX = p[0]; lastTapY = p[1];
				if (dbl) { zoomBy(2, p[0], p[1]); return; }
				click(p[0], p[1]);
				return;
			}
			// inertia from the last samples
			var s = d.samples;
			if (s.length >= 2) {
				var first = s[0], last = s[s.length - 1];
				var span = Math.max(1, last[0] - first[0]);
				if (now() - last[0] < 80) {
					var sumLng = 0, sumLat = 0, sumX = 0, sumY = 0;
					for (var j = 1; j < s.length; j++) { sumLng += s[j][1]; sumLat += s[j][2]; sumX += s[j][3]; sumY += s[j][4]; }
					if (view.t <= 0) inertia = { vx: sumLng / span, vy: sumLat / span };
					else inertia = { vx: sumX / span, vy: sumY / span };
					if (Math.abs(inertia.vx) + Math.abs(inertia.vy) < (view.t <= 0 ? 0.01 : 0.05)) inertia = null;
				}
			}
			invalidate();
			if (!inertia) scheduleSettle();
		}
		function click(sx, sy) {
			var hit = hitTest(sx, sy);
			if (!hit) { if (focusRef) setFocus("", "click"); return; }
			if (hit.kind === "artist") { setFocus("artist:" + hit.a.id, "click"); if (shell) shell.track("map_pin_click", { artist_id: hit.a.id }); return; }
			if (hit.kind === "cluster") {
				var m = hit.c.members, a0 = artists[m[0]], allSame = true, sumLat = 0, sumLng = 0;
				m.forEach(function (k) { var a = artists[k]; sumLat += a.lat; sumLng += a.lng; if (Math.abs(a.lat - a0.lat) > 1e-4 || Math.abs(a.lng - a0.lng) > 1e-4) allSame = false; });
				if (allSame && a0.group) { spreadGroups[a0.group[0]] = true; flyTo({ lat: a0.lat, lng: a0.lng, zoom: Math.max(view.zoom, 4) }); }
				else flyTo({ lat: sumLat / m.length, lng: sumLng / m.length, zoom: Math.min(ZOOM_MAX, view.zoom * 2.6) });
				touch();
				return;
			}
			if (hit.kind === "place") { setFocus("place:" + hit.p.path, "click"); if (shell) shell.track("map_place_click", { place_path: hit.p.path }); return; }
			if (hit.kind === "arc") { setFocus("artist:" + artists[hit.link.j].id, "click"); }
		}
		// No wheel zoom: the wheel scrolls the page. Zooming is the toolbar buttons,
		// the + and - keys, pinch and double-tap (the owner's call).
		var hoverKey = "";
		function onPointerLeave() { if (!dragging) { hideTip(); if (hoverRef) { hoverRef = null; hoverKey = ""; hoverArc = null; mapEl.classList.remove("is-hover"); invalidate(); } } }

		/* ---------- keyboard ---------- */

		function onKey(e) {
			if (e.altKey || e.ctrlKey || e.metaKey) return;
			var t = e.target;
			// Keys act on the stage itself. A button or link inside a card keeps its
			// own Enter and Space; only Escape and ? reach the map from there.
			if (t && t !== mapEl && e.key !== "Escape" && e.key !== "?") return;
			var step = 12 / Math.max(1, view.zoom);
			var handled = true;
			switch (e.key) {
				case "ArrowLeft": nudge(step, 0); break;
				case "ArrowRight": nudge(-step, 0); break;
				case "ArrowUp": nudge(0, step); break;
				case "ArrowDown": nudge(0, -step); break;
				case "+": case "=": zoomBy(1.3, view.w / 2, view.h / 2); break;
				case "-": case "_": zoomBy(1 / 1.3, view.w / 2, view.h / 2); break;
				case "0": resetView(); break;
				case "g": case "G": if (shell) shell.set("view", view.t >= 1 ? "globe" : "flat"); break;
				case "n": case "N": stepArtist(1); break;
				case "p": case "P": stepArtist(-1); break;
				case "Enter":
					if (focusRef && focusRef.kind === "artist") global.location.href = "/artist/" + focusRef.a.id + "/";
					else if (focusRef && focusRef.kind === "place" && focusRef.p.url) global.location.href = focusRef.p.url;
					else handled = false;
					break;
				case "f": case "F":
					if (focusRef && focusRef.kind === "artist") flyTo({ lat: focusRef.a.lat, lng: focusRef.a.lng, zoom: Math.max(view.zoom, 3.5) });
					else if (focusRef) flyTo({ lat: focusRef.p.lat, lng: focusRef.p.lng, zoom: zoomForPlace(focusRef.p) });
					break;
				case " ":
					if (tour) { tourToggle(); } else if (shell) shell.set("spin", state.spin === "0" ? "1" : "0");
					break;
				case "Escape": handled = escapeAction(); break;
				case "?": renderHelp(!helpOpen); break;
				default: handled = false;
			}
			if (handled) { e.preventDefault(); e.stopPropagation(); touch(); }
		}
		function nudge(dLng, dLat) {
			cancelAnims();
			inertia = null;
			if (view.t >= 1) { updateProjection(); var c = invertScreen(view.w / 2 + dLng * view.w / 90, view.h / 2 - dLat * view.h / 60); if (c) setCenter(c[0], c[1]); }
			else setCenter(view.lng + dLng, view.lat + dLat);
			invalidate();
			scheduleSettle();
		}

		/* ---------- tour ---------- */

		var scenes = null;
		function buildScenes() {
			var list = places.filter(function (p) { return placeHasPage(p) && p.artists >= 3; });
			list = list.filter(function (p) {
				// a parent holding exactly the same roster as a child is the vaguer name
				return !places.some(function (c) { return c.parent === p.path && c.artists === p.artists; });
			});
			list.sort(function (a, b) { return b.artists - a.artists || a.name.localeCompare(b.name); });
			var out = list.slice(0, 8).map(function (p) {
				var best = null;
				p.members.forEach(function (k) { if (!best || artists[k].score > best.score) best = artists[k]; });
				return { kind: "place", p: p, eyebrow: p.level === "country" ? "Country" : p.level === "region" ? "Region" : "City", title: p.name, url: p.url,
					text: plural(p.artists, "artist") + " on file, " + p.avg + "% average authorship" + (best ? ". Highest: " + best.name + " at " + best.score + "%." : "."),
					lat: p.lat, lng: p.lng, zoom: zoomForPlace(p) };
			});
			// the 100% Club: the city with the most artists who wrote every track on file
			var club = null;
			places.forEach(function (p) {
				if (p.level !== "city" && p.level !== "locality") return;
				var names = p.members.filter(function (k) { return artists[k].score === 100; }).map(function (k) { return artists[k].name; });
				if (names.length >= 2 && (!club || names.length > club.names.length)) club = { p: p, names: names };
			});
			if (club) out.push({ kind: "story", p: club.p, eyebrow: "The 100% Club", title: club.p.name, url: club.p.url, text: club.names.length + " artists from " + club.p.name + " wrote every track on file: " + joinNames(club.names) + ".", lat: club.p.lat, lng: club.p.lng, zoom: 6 });
			// the widest gap in one city
			var gap = null;
			places.forEach(function (p) {
				if ((p.level !== "city" && p.level !== "locality") || p.members.length < 2) return;
				var hi = null, lo = null;
				p.members.forEach(function (k) { var a = artists[k]; if (!hi || a.score > hi.score) hi = a; if (!lo || a.score < lo.score) lo = a; });
				if (hi.score - lo.score >= 50 && (!gap || hi.score - lo.score > gap.d)) gap = { p: p, hi: hi, lo: lo, d: hi.score - lo.score };
			});
			if (gap) out.push({ kind: "story", p: gap.p, eyebrow: "The widest gap", title: gap.p.name, url: gap.p.url, text: gap.hi.name + " at " + gap.hi.score + "% against " + gap.lo.name + " at " + gap.lo.score + "%, " + gap.d + " points apart in one town.", lat: gap.p.lat, lng: gap.p.lng, zoom: 6 });
			return out;
		}
		function renderTour() {
			// Keyboard focus rides across the re-render: the button with the same
			// action is focused again, and ending the tour hands focus to the stage.
			var act = tourEl.contains(document.activeElement) ? document.activeElement.getAttribute("data-map-act") : null;
			if (!tour) { tourEl.hidden = true; tourEl.innerHTML = ""; if (act) mapEl.focus(); return; }
			var s = scenes[tour.at];
			tourEl.innerHTML = '<span class="map-tour__eyebrow">' + esc(s.eyebrow) + '</span><h3 class="map-tour__title">' + (s.url ? '<a href="' + esc(s.url) + '">' + esc(s.title) + "</a>" : esc(s.title)) + "</h3>" +
				'<p class="map-tour__text">' + esc(s.text) + "</p>" +
				'<div class="map-tour__bar">' +
					'<button class="map-tour__btn" type="button" data-map-act="tour-prev" aria-label="Previous scene">' + icon("fa-backward-step") + "</button>" +
					'<button class="map-tour__btn" type="button" data-map-act="tour-toggle" aria-label="' + (tour.paused ? "Play" : "Pause") + '">' + icon(tour.paused ? "fa-play" : "fa-pause") + "</button>" +
					'<button class="map-tour__btn" type="button" data-map-act="tour-next" aria-label="Next scene">' + icon("fa-forward-step") + "</button>" +
					'<button class="map-tour__btn" type="button" data-map-act="tour-stop" aria-label="End the tour">' + icon("fa-xmark") + "</button>" +
					'<span class="map-tour__count">' + (tour.at + 1) + " / " + scenes.length + "</span>" +
				"</div>" +
				'<div class="map-tour__progress"><span data-tour-progress></span></div>';
			tourEl.hidden = false;
			if (act) { var back = tourEl.querySelector('[data-map-act="' + act + '"]'); if (back) back.focus(); }
		}
		function tourGo(at, silent) {
			if (!tour) return;
			tour.at = (at + scenes.length) % scenes.length;
			var s = scenes[tour.at];
			if (tour.hold) { clearTimeout(tour.hold); tour.hold = null; }
			if (tour.progress) { clearInterval(tour.progress); tour.progress = null; }
			renderTour();
			focusRef = null;
			renderFocus();
			say("Scene " + (tour.at + 1) + " of " + scenes.length + ": " + s.title + ". " + s.text);
			if (shell) { shell.set({ tour: "1", scene: String(tour.at) }, { silent: true }); if (opts.onStateChange) opts.onStateChange({ tour: "1", scene: String(tour.at) }); }
			var mine = tour.at, ms = silent ? 0 : FLY_MS;
			// The hold is armed here, for the fly plus the hold, not from the fly's
			// onDone: a drag or a key press during the fly cancels the fly, and the
			// tour must still move on.
			tourHold(ms);
			flyTo({ lat: s.lat, lng: s.lng, zoom: s.zoom, ms: ms, onDone: function () {
				if (!tour || tour.at !== mine) return;
				focusRef = { kind: "place", p: s.p };
				renderFocus();
				invalidate();
			} });
		}
		function tourHold(delay) {
			if (!tour || tour.paused) return;
			delay = delay || 0;
			var started = now() + delay;
			var bar = tourEl.querySelector("[data-tour-progress]");
			tour.progress = setInterval(function () {
				if (!bar || !tour) return;
				bar.style.setProperty("--progress", clamp(((now() - started) / TOUR_HOLD_MS) * 100, 0, 100).toFixed(1) + "%");
			}, 100);
			var advance = function () {
				tour.hold = null;
				if (!tour || tour.paused) return;
				// A finger still on the globe: look again shortly rather than fly out from under it.
				if (dragging) { tour.hold = setTimeout(advance, 800); return; }
				tourGo(tour.at + 1);
			};
			tour.hold = setTimeout(advance, delay + TOUR_HOLD_MS);
		}
		function startTour(at) {
			if (!scenes) scenes = buildScenes();
			if (!scenes.length) { say("No tour scenes yet: the map needs a place with three artists."); return; }
			var first = !tour;
			if (!tour) tour = { at: 0, paused: false, hold: null, progress: null };
			if (first && shell) shell.track("map_tour_start", { scene: at || 0 });
			tourGo(at == null ? 0 : at);
		}
		function tourToggle() {
			if (!tour) return;
			tour.paused = !tour.paused;
			if (tour.paused) { if (tour.hold) { clearTimeout(tour.hold); tour.hold = null; } if (tour.progress) { clearInterval(tour.progress); tour.progress = null; } }
			else tourHold();
			renderTour();
		}
		function stopTour() {
			if (!tour) return;
			if (tour.hold) clearTimeout(tour.hold);
			if (tour.progress) clearInterval(tour.progress);
			tour = null;
			renderTour();
			if (focusRef && focusRef.kind === "place") { focusRef = null; renderFocus(); }
			if (shell) { shell.set({ tour: "0", scene: "0" }, { silent: true }); if (opts.onStateChange) opts.onStateChange({ tour: "0", scene: "0" }); }
			say("Tour ended");
			invalidate();
		}

		/* ---------- borders and daylight ---------- */

		function loadBorders() {
			if (borders || bordersPending || !shell) return;
			bordersPending = true;
			shell.fetchJson("/assets/geo/world-countries-110m.json").then(function (topo) {
				borders = topojson.mesh(topo, topo.objects.countries, function (a, b) { return a !== b; });
				bordersPending = false;
				invalidate();
			}, function () { bordersPending = false; });
		}
		// Subsolar point from UTC: declination from the day of the year, hour angle
		// from the equation of time. Good to about a degree, which is all a shaded
		// hemisphere needs.
		function computeSun() {
			var d = new Date();
			var start = Date.UTC(d.getUTCFullYear(), 0, 0);
			var day = (d.getTime() - start) / 864e5;
			var g = (360 / 365.25) * (day - 81) * RAD;
			var decl = 23.44 * Math.sin(g);
			var eot = 9.87 * Math.sin(2 * g) - 7.53 * Math.cos(g) - 1.5 * Math.sin(g);
			var minutes = d.getUTCHours() * 60 + d.getUTCMinutes() + d.getUTCSeconds() / 60 + eot;
			var lng = wrapLng(180 - minutes / 4);
			sunPoint = [lng, decl];
		}
		function applyShade() {
			if (shadeTimer) { clearInterval(shadeTimer); shadeTimer = null; }
			if (state.shade === "day") {
				computeSun();
				shadeTimer = setInterval(function () { computeSun(); invalidate(); }, 60000);
			}
			invalidate();
		}

		/* ---------- state from the shell ---------- */

		var filterTimer = null, filterKeys = ["q", "genre", "tier", "era", "country", "type"];
		function trackFilter(key) {
			if (!shell) return;
			if (filterTimer) clearTimeout(filterTimer);
			filterTimer = setTimeout(function () { filterTimer = null; shell.track("map_filter_change", { filter_key: key, filter_value: state[key] || "all" }); }, 800);
		}
		function setState(next, changed) {
			if (next) state = next;
			changed = changed || [];
			var refilterNeeded = false, restyleNeeded = false, k;
			for (var j = 0; j < changed.length; j++) {
				k = changed[j];
				if (filterKeys.indexOf(k) !== -1) { refilterNeeded = true; trackFilter(k); }
				else if (k === "color" || k === "size") restyleNeeded = true;
				else if (k === "view") { morphTo(state.view === "flat"); if (shell) shell.track("map_view_change", { view: state.view, proj: state.proj }); }
				else if (k === "proj") { if (FLAT_RAW[state.proj]) { view.proj = state.proj; rawT = -1; invalidate(); } if (shell) shell.track("map_view_change", { view: state.view, proj: state.proj }); }
				else if (k === "borders") { if (state.borders === "1") loadBorders(); invalidate(); }
				else if (k === "shade") applyShade();
				else if (k === "focus") setFocus(state.focus, "url");
				else if (k === "arcs") { if (focusRef) { focusRef.since = now(); renderFocus(); } invalidate(); }
				else if (k === "panel") applyPanel();
				else if (k === "tour") { if (state.tour === "1") startTour(parseInt(state.scene, 10) || 0); else stopTour(); }
				else if (k === "scene") { if (tour && String(tour.at) !== state.scene) tourGo(parseInt(state.scene, 10) || 0); }
				else if (k === "lat" || k === "lng" || k === "zoom") { /* handled below, once */ }
				else if (k === "labels") { labelsStale = true; invalidate(); }
				else invalidate();
			}
			if (changed.indexOf("lat") !== -1 || changed.indexOf("lng") !== -1 || changed.indexOf("zoom") !== -1) {
				flyTo({ lat: state.lat === "" ? DEFAULT_CENTER.lat : +state.lat, lng: state.lng === "" ? DEFAULT_CENTER.lng : +state.lng, zoom: state.zoom === "" ? 1 : +state.zoom, ms: 500 });
			}
			if (changed.indexOf("spin") !== -1) touch();
			if (restyleNeeded) { restyle(); if (refilterNeeded) refilter(true); else publishRows(); }
			else if (refilterNeeded) refilter(true);
			if (!changed.length) { restyle(); refilter(false); }
			armIdle();
		}
		function action(key) {
			if (key === "tour-start") { if (tour) stopTour(); else startTour(0); }
			else if (key === "reset-view") resetView();
			else if (key === "help") renderHelp(true);
			else if (key === "escape") return escapeAction();
		}

		/* ---------- DOM wiring ---------- */

		function onStageClick(e) {
			var t = e.target;
			if (!t || !t.closest) return;
			var link = t.closest("[data-map-focus-artist]");
			if (link) { e.preventDefault(); setFocus("artist:" + link.getAttribute("data-map-focus-artist"), "click"); return; }
			var btn = t.closest("[data-map-act]");
			if (btn) {
				e.preventDefault();
				var act = btn.getAttribute("data-map-act");
				if (act === "clear") setFocus("", "click");
				else if (act === "fly") { if (focusRef) flyTo(focusRef.kind === "artist" ? { lat: focusRef.a.lat, lng: focusRef.a.lng, zoom: Math.max(view.zoom, 3.5) } : { lat: focusRef.p.lat, lng: focusRef.p.lng, zoom: zoomForPlace(focusRef.p) }); }
				else if (act === "arcs") { if (shell) shell.set("arcs", state.arcs === "0" ? "1" : "0"); }
				else if (act === "zoom-in") zoomBy(1.4, view.w / 2, view.h / 2);
				else if (act === "zoom-out") zoomBy(1 / 1.4, view.w / 2, view.h / 2);
				else if (act === "home") resetView();
				else if (act === "fullscreen") setFullscreen(!fullscreen);
				else if (act === "panel") { if (shell) shell.set("panel", state.panel === "0" ? "1" : "0", { silent: true }); else state.panel = state.panel === "0" ? "1" : "0"; applyPanel(); }
				else if (act === "help-close") { renderHelp(false); mapEl.focus(); }
				else if (act === "release") setCaptured(false, true);
				else if (act === "tour-prev") tourGo(tour ? tour.at - 1 : 0);
				else if (act === "tour-next") tourGo(tour ? tour.at + 1 : 0);
				else if (act === "tour-toggle") tourToggle();
				else if (act === "tour-stop") stopTour();
				touch();
				return;
			}
			var row = t.closest(".tool-result");
			if (row && !t.closest("a")) {
				var a = row.querySelector(".tool-result__name");
				var href = a && a.getAttribute("href");
				var m = href && /^\/artist\/([^/]+)\/$/.exec(href);
				if (m) setFocus("artist:" + m[1], "click");
			}
		}

		canvas.addEventListener("pointerdown", onPointerDown);
		canvas.addEventListener("pointermove", onPointerMove);
		canvas.addEventListener("pointerup", onPointerUp);
		canvas.addEventListener("pointercancel", onPointerUp);
		canvas.addEventListener("pointerleave", onPointerLeave);
		mapEl.addEventListener("keydown", onKey);
		stage.addEventListener("click", onStageClick);
		canvas.addEventListener("dblclick", function (e) { e.preventDefault(); });
		var ro = global.ResizeObserver ? new global.ResizeObserver(function () { resize(); }) : null;
		if (ro) ro.observe(mapEl); else global.addEventListener("resize", resize);
		var io = global.IntersectionObserver ? new global.IntersectionObserver(function (entries) {
			active = entries[0] ? entries[0].isIntersecting : true;
			if (active) { lastStepAt = 0; invalidate(); }
			else if (touchGate && captured) setCaptured(false);
		}, { threshold: 0 }) : null;
		if (io) io.observe(mapEl);
		function onVisibility() { pageVisible = !document.hidden; if (pageVisible) { lastFrameAt = 0; lastStepAt = 0; invalidate(); } }
		document.addEventListener("visibilitychange", onVisibility);
		// The layer's dialog scales in over 180 ms; the layout size the canvas is
		// sized from does not change, but a size read before the first paint can,
		// so the stage is measured again once the animation is over.
		var dialogEl = isLayer && stage.closest ? stage.closest("dialog") : null;
		function onDialogSettled(e) { if (!e || e.target === dialogEl) resize(); }
		if (dialogEl) dialogEl.addEventListener("animationend", onDialogSettled);

		/* ---------- boot ---------- */

		renderToolbar();
		setCaptured(false);
		applyPanel();
		fillCountryOptions();
		fillChipCounts();
		resize();
		if (state.lat !== "" && isFinite(+state.lat)) view.lat = clamp(+state.lat, -89.5, 89.5);
		if (state.lng !== "" && isFinite(+state.lng)) view.lng = wrapLng(+state.lng);
		if (state.zoom !== "" && isFinite(+state.zoom)) view.zoom = clamp(+state.zoom, ZOOM_MIN, ZOOM_MAX);
		restyle();
		refilter(false);
		if (state.borders === "1") loadBorders();
		applyShade();
		if (state.focus) setFocus(state.focus, state.lat !== "" ? "url-no-fly" : "url");
		if (state.tour === "1") startTour(parseInt(state.scene, 10) || 0);
		lastInteraction = now() - IDLE_MS + 1500;
		armIdle();
		invalidate();

		function destroy() {
			destroyed = true;
			if (rafId) global.cancelAnimationFrame(rafId);
			if (idleTimer) clearTimeout(idleTimer);
			if (settleTimer) clearTimeout(settleTimer);
			if (shadeTimer) clearInterval(shadeTimer);
			if (filterTimer) clearTimeout(filterTimer);
			if (tour) { if (tour.hold) clearTimeout(tour.hold); if (tour.progress) clearInterval(tour.progress); tour = null; }
			if (ro) ro.disconnect(); else global.removeEventListener("resize", resize);
			if (io) io.disconnect();
			if (dialogEl) dialogEl.removeEventListener("animationend", onDialogSettled);
			document.removeEventListener("visibilitychange", onVisibility);
			mapEl.removeEventListener("keydown", onKey);
			stage.removeEventListener("click", onStageClick);
			pulseEl.hidden = true;
		}

		return {
			setState: setState,
			getState: function () { return state; },
			focus: function (target) { setFocus(target || "", "api"); },
			flyTo: flyTo,
			rows: function () { return currentRows; },
			startTour: function () { startTour(0); },
			stopTour: stopTour,
			action: action,
			resize: resize,
			destroy: destroy,
			view: view,
			// For DevTools and the Playwright checks: screen <-> geographic, and what is on screen.
			project: function (g) { updateProjection(); return projection(g); },
			invert: function (s) { updateProjection(); return invertScreen(s[0], s[1]); },
			probe: function () {
				return { t: view.t, zoom: view.zoom, lat: view.lat, lng: view.lng, w: view.w, h: view.h, dpr: view.dpr, active: active, sun: sunPoint, shown: visible.length, spinning: spinning, tour: !!tour, quality: quality, captured: captured, motionLand: !!landMotion,
					focus: focusRef ? (focusRef.kind === "artist" ? "artist:" + focusRef.a.id : "place:" + focusRef.p.path) : "",
					clusters: clusters.map(function (c) { return { x: c.x, y: c.y, count: c.members.length }; }),
					labels: labelsDrawn.filter(function (l) { return l.p; }).map(function (l) { return l.p.path; }),
					labelBoxes: labelsDrawn.filter(function (l) { return l.p && l.box; }).map(function (l) { return { path: l.p.path, box: l.box }; }) };
			}
		};
	}

	/* ---------- the overlay ---------- */

	// openOverlay({ launcher, subject, source, tool, index, world }) opens the
	// layer on any page. `tool` is /assets/data/map-tool.json (the same object
	// /map/ carries in data-tool-config), `subject` is "artist:<id>",
	// "place:<path>" or "all". Returns the layer api from the shell.
	function openOverlay(o) {
		var Shell = global.SongSueyToolShell;
		if (!Shell) throw new Error("map-engine: tool-shell.js must load first");
		var tool = o.tool || {};
		var index = o.index, world = o.world;
		var subject = o.subject && o.subject !== "all" ? o.subject : "";
		// Where the open came from, for analytics: the launcher's page, or a link
		// carrying the #map= fragment when there is no launcher.
		var here = global.location.pathname;
		var source = o.source || (!o.launcher ? "link" : here === "/" ? "home" : here.indexOf("/origin/") === 0 ? "place" : "artist");
		// Reopened from a shared link: the fragment already carries the focus.
		var frag = Shell.readLayerFragment();
		if (!subject && frag && frag.id === "map") subject = Shell.parseQuery(frag.query).focus || "";
		var name = tool.name || "Artist Map";
		var initial = {};
		if (subject) {
			initial.focus = subject;
			var cols = index && index.cols ? index.cols : {};
			if (subject.indexOf("artist:") === 0 && index) {
				var ai = (cols.artists || []).indexOf("id"), an = (cols.artists || []).indexOf("name");
				var row = index.artists.filter(function (r) { return r[ai] === subject.slice(7); })[0];
				if (row) name = row[an] + " on the map";
			} else if (subject.indexOf("place:") === 0 && index) {
				var pp = (cols.places || []).indexOf("path"), pn = (cols.places || []).indexOf("name");
				var prow = index.places.filter(function (r) { return r[pp] === subject.slice(6); })[0];
				if (prow) name = prow[pn] + " on the map";
				initial.spin = "0";
			}
		}
		var engine = null;
		var layer = Shell.openLayer({
			id: "map",
			config: tool,
			name: name,
			state: initial,
			fullPageUrl: tool.url || "/map/",
			launcher: o.launcher || null,
			noun: "artists",
			columns: tool.columns,
			onMount: function (shell, stage, api) {
				stage.innerHTML = stageMarkup();
				engine = create(stage, { config: tool, index: index, world: world, state: shell.state, shell: shell, layer: true });
				global.SongSueyMapEngine.layer = { shell: shell, engine: engine };
				// Escape from anywhere in the layer (a panel control included) clears
				// the help card, the tour or the focus first; only a further press,
				// with nothing left to clear, reaches the dialog and closes it.
				if (api && api.dialog) api.dialog.addEventListener("keydown", function (e) {
					if (e.key !== "Escape" || e.altKey || e.ctrlKey || e.metaKey || !engine) return;
					if (engine.action("escape")) { e.preventDefault(); e.stopPropagation(); }
				});
				shell.track("map_open", { source: source, subject: subject || "all" });
			},
			onChange: function (state, changed) { if (engine) engine.setState(state, changed); },
			onAction: function (key) { if (engine) engine.action(key); },
			onClose: function () { if (engine) { engine.destroy(); engine = null; } global.SongSueyMapEngine.layer = null; }
		});
		return layer;
	}

	/* ---------- /map/ page boot ---------- */

	// On the page the controls float over the map in two corner cards (the
	// tool--overlaid layout): View top left with the Layers and Advanced groups
	// behind a "More settings" disclosure, Roster and the export bar bottom left.
	// The layer keeps its panel column, so this only runs on the page. Moving the
	// nodes is safe: the shell binds every control by delegation on the root.
	// Hidden controls, the way the big globe tools do it (Cesium's viewer toolbar,
	// Google Maps' default UI): every control sits behind an icon on a rail at the
	// top left and opens as a popover on demand, one at a time, closed by Escape,
	// the same icon, its own close button, or a click anywhere else. Zoom, reset
	// and fullscreen keep the top right, the legend the bottom right, and a small
	// pill at the bottom left carries the live count. The layer keeps its panel
	// column, so this only runs on the page. Moving the nodes is safe: the shell
	// binds every control by delegation on the root.
	var FILTER_KEYS = ["genre", "tier", "era", "country", "type"];
	var RAIL = [
		{ id: "search", icon: "fa-magnifying-glass", label: "Search the roster", tip: "Search" },
		{ id: "display", icon: "fa-layer-group", label: "Display: view, colors, layers", tip: "Display" },
		{ id: "filters", icon: "fa-filter", label: "Filter the roster", tip: "Filters" },
		{ id: "tour", icon: "fa-play", label: "Start or stop the tour", tip: "Tour", act: "tour-start" },
		{ id: "download", icon: "fa-download", label: "Download or share this view", tip: "Download and share" },
		{ id: "help", icon: "fa-keyboard", label: "Keyboard shortcuts", tip: "Shortcuts (?)", act: "help" }
	];
	function railIcon(name) {
		return '<svg class="icon" focusable="false" aria-hidden="true"><use href="/assets/icons/sprite.svg#' + name + '"></use></svg>';
	}
	function arrangeRail(root) {
		if (!root.classList.contains("tool--overlaid")) return;
		var controls = root.querySelector("[data-tool-controls]");
		var exportBar = root.querySelector("[data-tool-export-bar]");
		if (!controls || root.querySelector(".tool-rail")) return;
		// 1. group the rendered controls by their section heading
		var groups = {}, current = null, node = controls.firstElementChild, next;
		while (node) {
			next = node.nextElementSibling;
			if (node.classList.contains("tool-controls__section")) current = node.textContent.trim();
			if (current) {
				if (!groups[current]) { groups[current] = document.createElement("div"); groups[current].className = "tool-popover__group"; }
				groups[current].appendChild(node);
			}
			node = next;
		}
		// 2. the rail and one popover per rail entry that carries content
		var railEl = document.createElement("div");
		railEl.className = "tool-rail";
		railEl.setAttribute("role", "toolbar");
		railEl.setAttribute("aria-label", "Map controls");
		var pops = {};
		RAIL.forEach(function (r) {
			var btn = document.createElement("button");
			btn.type = "button";
			btn.className = "tool-rail__btn";
			btn.setAttribute("aria-label", r.label);
			btn.setAttribute("data-tip", r.tip);
			btn.setAttribute("data-tip-side", "right");
			btn.innerHTML = railIcon(r.icon);
			if (r.act) btn.setAttribute("data-rail-act", r.act);
			else {
				btn.setAttribute("data-tool-pop", r.id);
				btn.setAttribute("aria-expanded", "false");
				btn.setAttribute("aria-controls", "map-pop-" + r.id);
				var pop = document.createElement("div");
				pop.className = "tool-popover tool-popover--" + r.id;
				pop.id = "map-pop-" + r.id;
				pop.setAttribute("role", "dialog");
				pop.setAttribute("aria-label", r.label);
				pop.hidden = true;
				pop.innerHTML = '<div class="tool-popover__head"><span class="tool-popover__title">' + r.tip + '</span><button class="tool-popover__close" type="button" data-tool-pop-close aria-label="Close">' + railIcon("fa-xmark") + "</button></div>";
				pops[r.id] = pop;
			}
			if (r.id === "filters") { var badge = document.createElement("span"); badge.className = "tool-rail__badge"; badge.setAttribute("data-rail-badge", ""); badge.hidden = true; btn.appendChild(badge); }
			railEl.appendChild(btn);
		});
		// 3. fill the popovers
		var roster = groups.Roster;
		if (roster) {
			var q = roster.querySelector('[data-tool-field="q"]');
			if (q) pops.search.appendChild(q);
			var heading = roster.querySelector(".tool-controls__section");
			if (heading) heading.parentNode.removeChild(heading);
			pops.filters.appendChild(roster);
			// The clear button lives in the sticky header, so it is one reach away
			// however far the filters scroll; it greys out when nothing is on.
			var clear = document.createElement("button");
			clear.type = "button";
			clear.className = "tool-popover__clear tool-popover__clear--head";
			clear.setAttribute("data-map-clear-filters", "");
			clear.setAttribute("data-tip", "Every filter back to All");
			clear.setAttribute("data-tip-side", "right");
			clear.textContent = "Clear filters";
			var filtersHead = pops.filters.querySelector(".tool-popover__head");
			filtersHead.insertBefore(clear, filtersHead.querySelector("[data-tool-pop-close]"));
		}
		if (groups.View) pops.display.appendChild(groups.View);
		if (groups.Layers) pops.display.appendChild(groups.Layers);
		if (groups.Advanced) groups.Advanced.parentNode && groups.Advanced.parentNode.removeChild(groups.Advanced);
		if (exportBar) {
			var count = exportBar.querySelector("[data-tool-count]");
			var status = document.createElement("div");
			status.className = "tool-status";
			if (count) status.appendChild(count);
			var clearPill = document.createElement("button");
			clearPill.type = "button";
			clearPill.className = "tool-status__clear";
			clearPill.setAttribute("data-map-clear-filters", "");
			clearPill.hidden = true;
			clearPill.textContent = "Clear filters";
			status.appendChild(clearPill);
			controls.appendChild(status);
			pops.download.appendChild(exportBar);
		}
		controls.appendChild(railEl);
		Object.keys(pops).forEach(function (k) { controls.appendChild(pops[k]); });
		// 4. behaviour: one popover at a time, Escape and outside clicks close it
		function openPop(id) {
			RAIL.forEach(function (r) {
				if (!pops[r.id]) return;
				var on = r.id === id;
				pops[r.id].hidden = !on;
				var b = railEl.querySelector('[data-tool-pop="' + r.id + '"]');
				if (b) b.setAttribute("aria-expanded", on ? "true" : "false");
			});
			root.classList.toggle("has-popover", !!id);
			if (id) {
				var first = pops[id].querySelector("input, select, button:not([data-tool-pop-close])");
				if (first) first.focus();
			}
		}
		function openId() { for (var k in pops) if (pops[k] && !pops[k].hidden) return k; return null; }
		root.addEventListener("click", function (e) {
			var t = e.target;
			if (!t || !t.closest) return;
			var pb = t.closest("[data-tool-pop]");
			if (pb) { var id = pb.getAttribute("data-tool-pop"); openPop(openId() === id ? null : id); return; }
			if (t.closest("[data-tool-pop-close]")) { var was = openId(); openPop(null); if (was) { var b = railEl.querySelector('[data-tool-pop="' + was + '"]'); if (b) b.focus(); } return; }
			var ab = t.closest("[data-rail-act]");
			if (ab) { var page = global.SongSueyMapEngine.page; if (page && page.engine) page.engine.action(ab.getAttribute("data-rail-act")); return; }
			var cb = t.closest("[data-map-clear-filters]");
			if (cb) { var page2 = global.SongSueyMapEngine.page; if (page2 && page2.shell) { var patch = {}; FILTER_KEYS.forEach(function (k) { patch[k] = ""; }); page2.shell.set(patch); } return; }
		});
		document.addEventListener("pointerdown", function (e) {
			if (!openId()) return;
			var t = e.target;
			if (t && t.closest && (t.closest(".tool-popover") || t.closest(".tool-rail"))) return;
			openPop(null);
		}, true);
		document.addEventListener("keydown", function (e) {
			if (e.key !== "Escape") return;
			var was = openId();
			if (!was) return;
			e.stopPropagation();
			openPop(null);
			var b = railEl.querySelector('[data-tool-pop="' + was + '"]');
			if (b) b.focus();
		}, true);
	}
	// The filters badge and the bottom-left pill both say how many roster filters
	// are away from their defaults, so a hidden control never hides a filter.
	function updateFilterBadge(root, state) {
		var on = 0;
		for (var i = 0; i < FILTER_KEYS.length; i++) if (state[FILTER_KEYS[i]]) on++;
		var badge = root.querySelector("[data-rail-badge]");
		if (badge) { badge.textContent = String(on); badge.hidden = on === 0; }
		var clear = root.querySelector(".tool-status__clear");
		if (clear) clear.hidden = on === 0;
		var headClear = root.querySelector(".tool-popover--filters [data-map-clear-filters]");
		if (headClear) headClear.disabled = on === 0;
		var fb = root.querySelector('[data-tool-pop="filters"]');
		if (fb) fb.classList.toggle("is-active", on > 0);
	}

	function bootPage() {
		var Shell = global.SongSueyToolShell;
		var root = document.getElementById("toolApp");
		if (!Shell || !root || root.getAttribute("data-tool") !== "map") return;
		arrangeRail(root);
		var engine = null;
		var shell = Shell.mount({
			root: root,
			onChange: function (state, changed) { updateFilterBadge(root, state); if (engine) engine.setState(state, changed); },
			onAction: function (key) { if (engine) engine.action(key); }
		});
		updateFilterBadge(root, shell.state);
		var config = shell.config || {};
		var worldUrl = (config.world && config.world[0]) || "/assets/geo/world-land-110m.json";
		var loading = root.querySelector("[data-map-loading]");
		Promise.all([shell.fetchJson(root.getAttribute("data-tool-index") || config.index), shell.fetchJson(worldUrl)]).then(function (files) {
			engine = create(shell.stage, { config: config, index: files[0], world: files[1], state: shell.state, shell: shell, layer: false });
			global.SongSueyMapEngine.page = { shell: shell, engine: engine };
			shell.track("map_open", { source: "page", subject: shell.state.focus || "all" });
		}, function (err) {
			if (loading) { loading.textContent = "The map could not load its data (" + err.message + "). Reload the page to try again."; loading.classList.add("is-error"); }
			shell.results([], { label: "Could not load the index" });
		});
	}

	global.SongSueyMapEngine = {
		create: create,
		openOverlay: openOverlay,
		schema: schema,
		stageMarkup: stageMarkup,
		GENRE_FACETS: GENRE_FACETS,
		TIER_FACETS: TIER_FACETS,
		ERA_FACETS: ERA_FACETS
	};

	if (typeof document !== "undefined" && document.getElementById) {
		if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bootPage);
		else bootPage();
	}
})(typeof window !== "undefined" ? window : this);
